import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import EventSectionTabs, { EVENT_TAB_CALENDAR } from "../components/EventSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import TabsList from "../components/TabsList";
import dayjs from "dayjs";
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from "firebase/auth";
import { validateEmail, validatePhone, formatValidationErrorList } from "../shared/utils/validation";
import { formatUsPhoneAsYouType } from "../shared/utils/phoneFormat";
import EventRegistrationQRCode from "../components/EventRegistrationQRCode";
import PhoneInput from "../components/PhoneInput";
import PasswordInput from "../components/PasswordInput";
import ConfirmationModal from "../components/ConfirmationModal";
import CoppaPrivacyConsentBlock from "../components/CoppaPrivacyConsentBlock";
import SmsOptInConsentBlock from "../components/SmsOptInConsentBlock";
import ModalShell from "../components/ModalShell";
import PhotographyConsentBlock from "../components/PhotographyConsentBlock";
import EventRegistrationConfirmationBody, {
  mapParentApiResultsToOutcomeRows,
} from "../components/EventRegistrationConfirmationBody";
import RegisteredEventHeroCard from "../components/RegisteredEventHeroCard";
import ParticipantCheckInQRPanel from "../components/ParticipantCheckInQRPanel";
import EventDescriptionPreview from "../components/EventDescriptionPreview";
import EventCardPriceMeta from "../components/EventCardPriceMeta";
import EventPriceBadge from "../components/EventPriceBadge";
import EventScheduleCalendar from "../components/EventScheduleCalendar";
import GlobalTimeNavigatorBar from "../components/GlobalTimeNavigatorBar";
import ViewModeIconToggle from "../components/ViewModeIconToggle";
import { IconCalendar, IconClock, IconLocation, IconSave, IconDelete, IconParticipants, IconCancel, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { getCurrentSubdomain } from "../shared/utils/subdomain";
import {
  allowsFamilyParentSignup,
  allowsStudentYouthSignup,
  DEFAULT_EVENT_SIGNUP_MODE,
  eventSignupModeAllowsAnySignup,
  normalizeAccountEventSignupMode,
  resolveEventSignupGatewayMode,
} from "../shared/utils/accountEventSignupMode";
import { ACCOUNT_SIGNUP_FORM_CLASS, ACCOUNT_SIGNUP_MODAL_MAX_WIDTH } from "../shared/utils/accountSignupForm";
import { getUpcomingEventsSorted } from "../shared/utils/eventUpcoming";
import { buildEventCalendarLoginReturnPath } from "../shared/utils/eventCalendarNavigation";
import {
  EVENT_PLACEHOLDER_REGISTRATION_MESSAGE,
  EVENT_PLACEHOLDER_TBA,
  formatPlaceholderAwareEventDate,
  formatPlaceholderAwareEventLocation,
  isPlaceholderEvent,
} from "../shared/utils/eventPlaceholder";
import { resolveEventBackgroundImageUrl } from "../shared/utils/eventBackgroundImage";
import {
  blocksPaidBulkRegistration,
} from "../shared/utils/eventPaymentMode";
import {
  formatMyRegistrationStatusLabel,
  getMyEventsParticipantDisplay,
  getRegisteredEventParticipantChildLabel,
} from "../shared/utils/eventRegistrationDisplay";
import { buildEventCalendarParticipantQrEntries } from "../shared/utils/eventCalendarParticipantQr";
import {
  isYouthOnlyEvent,
  parentIsRegistrableAttendeeForEvent,
  shouldIncludeParentSelfAttendee,
  shouldOpenParentRegistrationFlow,
  userInfoHasParentRole,
} from "../shared/utils/eventAudienceRegistration";
import {
  getBirthMonthYearInputValue,
  normalizeBirthMonthYearForSave,
  validateRequiredBirthMonthYear,
} from "../shared/utils/birthMonthYear";
import GenderSelect from "../components/GenderSelect";
import RaceEthnicityMultiSelect from "../components/RaceEthnicityMultiSelect";
import BirthMonthYearInput from "../components/BirthMonthYearInput";
import { validateRequiredGender } from "../shared/utils/genderOptions";
import {
  buildChildProfilePayload,
  validateRequiredRaceEthnicity,
  validateRequiredSchoolDistrict,
} from "../shared/utils/childProfileOptions";
import { GRADE_OPTIONS, getGradeLabel, mapGradeValue } from "../shared/utils/gradeOptions";
import {
  EDUCATION_LEVEL_OPTIONS,
  HEAR_ABOUT_US_OPTIONS,
  HEAR_ABOUT_US_OTHER_VALUE,
  mapEducationLevelValue,
  mapHearAboutUsValue,
  validateRequiredEducationLevel,
  validateRequiredHearAboutUs,
  validateRequiredUsZip,
} from "../shared/utils/parentProfileOptions";
import {
  validateRequiredPhotographyConsent,
} from "../shared/utils/photographyConsent";
import { auth } from "../firebase";
import { serverInfo } from "../shared/config/serverConfig";
import TabLabelWithCount from "../components/TabLabelWithCount";
import OpenClosedFilterDropdown from "../components/OpenClosedFilterDropdown";
import TableShell, { SortableTableTh, TableActionColumnHeader } from "../components/TableShell";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { useTableSort } from "../shared/hooks/useTableSort";
import { filterEventsByOpenClosedStatus, getEventUiPhase } from "../shared/utils/eventOpenClosedStatus";
import { formatTimestampForTable } from "../shared/utils/dateDisplay";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");
const { useAuth } = require("../contexts/AuthContext");
const { displayEmailOrDash } = require("../shared/utils/displayEmail");

const PROGRAM_FILTER_ALL = "";
const PROGRAM_FILTER_NONE = "__none__";
const EVENT_CALENDAR_LIST_SORT_INITIAL = { key: "start_datetime", direction: "asc" };

function normalizeProgramName(label) {
  return label != null ? String(label).trim() : "";
}

function getEventProgramFilterId(ev) {
  const pid = ev?.program_id != null ? String(ev.program_id).trim() : "";
  if (pid) return pid;
  const name = normalizeProgramName(ev?.program_name);
  if (name) return `__name__:${name.toLowerCase()}`;
  return PROGRAM_FILTER_NONE;
}

function getEventProgramLabel(ev, programById) {
  const pid = ev?.program_id != null ? String(ev.program_id).trim() : "";
  const directName = normalizeProgramName(ev?.program_name);
  if (directName) return directName;
  if (pid && programById && programById[pid] && programById[pid].name) {
    const fromLookup = normalizeProgramName(programById[pid].name);
    if (fromLookup) return fromLookup;
  }
  if (pid) return `Program (${pid.slice(0, 6)}…)`;
  return "No program";
}

/** Open / Expired / Closed badge — same colors as Events table. */
function EventCalendarOpenClosedStatusBadge({ event }) {
  const phase = getEventUiPhase(event);
  if (phase === "Expired") {
    return (
      <span className="badge" style={{ background: "#fef2f2", color: "#dc2626" }}>
        Expired
      </span>
    );
  }
  if (phase === "Closed") {
    return (
      <span className="badge" style={{ background: "#f3f4f6", color: "#374151" }}>
        Closed
      </span>
    );
  }
  return (
    <span className="badge" style={{ background: "#f0fdf4", color: "#16a34a" }}>
      Open
    </span>
  );
}

/** Free-text filter for Event Calendar "Available" list events. */
function eventCalendarAvailableMatchesSearch(ev, searchLower) {
  if (!searchLower) return true;
  const programName =
    ev.program_name != null && ev.program_name !== "" ? String(ev.program_name) : "";
  const organizer =
    ev.organizer_name != null && ev.organizer_name !== "" ? String(ev.organizer_name) : "";
  const audience = (ev.audience_type || "").trim().toLowerCase();
  const perfectFor = (ev.perfect_for || "").trim().toLowerCase();
  const statusStr = (ev.status || "").toLowerCase();
  return (
    (ev.name && ev.name.toLowerCase().includes(searchLower)) ||
    (ev.description && ev.description.toLowerCase().includes(searchLower)) ||
    (ev.location && ev.location.toLowerCase().includes(searchLower)) ||
    (programName && programName.toLowerCase().includes(searchLower)) ||
    (organizer && organizer.toLowerCase().includes(searchLower)) ||
    (audience && audience.includes(searchLower)) ||
    (perfectFor && perfectFor.includes(searchLower)) ||
    (statusStr && statusStr.includes(searchLower))
  );
}

/** Free-text filter for getMyEvents rows (My Registered Events tab). */
function eventCalendarRegistrationRowMatchesSearch(ev, loggedInUserIdStr, searchLower) {
  if (!searchLower) return true;
  const programName =
    ev.program_name != null && ev.program_name !== "" ? String(ev.program_name) : "";
  const organizer =
    ev.organizer_name != null && ev.organizer_name !== "" ? String(ev.organizer_name) : "";
  const statusStr = (ev.status || "Open").toLowerCase();
  const participantDisp = (getMyEventsParticipantDisplay(ev, loggedInUserIdStr) || "").toLowerCase();
  const attendanceStr = (ev.attendance || "Registered").toLowerCase();
  const audience = (ev.audience_type || "").trim().toLowerCase();
  const perfectFor = (ev.perfect_for || "").trim().toLowerCase();
  return (
    (ev.name && ev.name.toLowerCase().includes(searchLower)) ||
    (ev.description && ev.description.toLowerCase().includes(searchLower)) ||
    (ev.location && ev.location.toLowerCase().includes(searchLower)) ||
    (programName && programName.toLowerCase().includes(searchLower)) ||
    (organizer && organizer.toLowerCase().includes(searchLower)) ||
    (statusStr && statusStr.includes(searchLower)) ||
    (participantDisp && participantDisp.includes(searchLower)) ||
    ((ev.participant_name || "").toLowerCase().includes(searchLower)) ||
    (attendanceStr && attendanceStr.includes(searchLower)) ||
    (audience && audience.includes(searchLower)) ||
    (perfectFor && perfectFor.includes(searchLower))
  );
}

/** Action-oriented titles for informational alerts (avoid generic "Info"). */
const ALERT_TITLE_SELECT_EVENTS_FIRST = "Select events first";
const ALERT_TITLE_EVENTS_UNAVAILABLE = "Events no longer available";
const ALERT_TITLE_REGISTER_PAID_INDIVIDUALLY = "Register paid events individually";
const ALERT_TITLE_ADD_REQUIRED_INFO = "Add required information";
const ALERT_TITLE_REGISTRATION_UNAVAILABLE = "Registration unavailable";
const ALERT_TITLE_PAYMENT_COULD_NOT_START = "Payment could not start";
const ALERT_TITLE_REGISTRATION_FAILED = "Registration failed";
const ALERT_TITLE_NO_REGISTRATIONS_CREATED = "No registrations completed";
const ALERT_TITLE_NO_EVENTS_SELECTED = "No events selected";
const ALERT_TITLE_CANNOT_CANCEL = "Cannot cancel registration";
const ALERT_TITLE_CHECK_DETAILS = "Check the details";

const REGISTRATION_EMAIL_CODE_NOTE =
  "You'll receive an email with a code to bring with you on the day of your registered event.";

/** Decorative art for “My Registered Events” when signed out: calendar, passport, flight (inline SVG). */
function EventCalendarRegisteredGuestIllustration() {
  const navy = "#1e3a5f";
  const navyMid = "#2c4a6e";
  const blue = "#2c5282";
  const gridLine = "#dee2e6";
  return (
    <svg
      className="EventCalendar-registered-guest-svg"
      viewBox="0 0 320 200"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ec-cal-header" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={navy} />
          <stop offset="100%" stopColor={navyMid} />
        </linearGradient>
      </defs>
      {/* Panel */}
      <rect x="6" y="6" width="308" height="188" rx="14" fill="#eef1f4" stroke="#dee2e6" strokeWidth="1" />

      {/* — Calendar (back left) — */}
      <g transform="translate(22, 24)">
        <rect x="0" y="18" width="108" height="118" rx="8" fill="#fff" stroke="#ced4da" strokeWidth="1" />
        <rect x="14" y="0" width="10" height="26" rx="5" fill="#adb5bd" />
        <rect x="49" y="0" width="10" height="26" rx="5" fill="#adb5bd" />
        <rect x="84" y="0" width="10" height="26" rx="5" fill="#adb5bd" />
        <rect x="8" y="26" width="92" height="16" rx="3" fill="url(#ec-cal-header)" />
        <text x="54" y="38" textAnchor="middle" fill="#fff" fontSize="9" fontFamily="system-ui, sans-serif" fontWeight="600">
          EVENTS
        </text>
        {[0, 1, 2, 3].flatMap((row) =>
          [0, 1, 2, 3].map((col) => {
            const cx = 16 + col * 22;
            const cy = 50 + row * 18;
            const isHighlight = row === 1 && col === 2;
            return (
              <rect
                key={`c-${row}-${col}`}
                x={cx}
                y={cy}
                width="18"
                height="14"
                rx="2"
                fill={isHighlight ? "#c5d4eb" : "#f8f9fa"}
                stroke={gridLine}
                strokeWidth="0.75"
              />
            );
          })
        )}
      </g>

      {/* — Passport (center, in front) — */}
      <g transform="translate(124, 42)">
        <rect x="0" y="0" width="72" height="98" rx="7" fill={navy} />
        <rect x="5" y="6" width="62" height="86" rx="5" fill={blue} />
        {/* Globe / seal */}
        <circle cx="36" cy="26" r="11" fill="none" stroke="#94a3b8" strokeWidth="1.25" />
        <ellipse cx="36" cy="26" rx="11" ry="4.5" fill="none" stroke="#94a3b8" strokeWidth="0.9" />
        <line x1="25" y1="26" x2="47" y2="26" stroke="#94a3b8" strokeWidth="0.9" />
        <line x1="36" y1="15" x2="36" y2="37" stroke="#94a3b8" strokeWidth="0.9" />
        {/* Photo + lines */}
        <rect x="22" y="44" width="28" height="30" rx="2" fill="#e9ecef" stroke="#adb5bd" strokeWidth="0.75" />
        <circle cx="36" cy="54" r="5" fill="#cbd5e1" />
        <rect x="18" y="78" width="36" height="2.5" rx="1" fill="#94a3b8" />
        <rect x="22" y="84" width="28" height="2.5" rx="1" fill="#94a3b8" />
      </g>

      {/* — Flight path + airplane (upper right) — */}
      <path
        d="M 198 118 Q 232 42 288 28"
        fill="none"
        stroke="#cbd5e1"
        strokeWidth="2"
        strokeDasharray="5 5"
      />
      <g transform="translate(238, 22) rotate(-12 20 18)">
        {/* Fuselage */}
        <path
          d="M 8 28 L 52 24 L 58 28 L 52 32 L 8 28 Z"
          fill={navy}
        />
        <path d="M 22 28 L 38 18 L 42 28 L 38 38 Z" fill={blue} />
        <path d="M 52 28 L 62 22 L 60 34 Z" fill={blue} />
        {/* Nose */}
        <path d="M 58 28 L 68 28 L 58 31 Z" fill={navyMid} />
      </g>

      {/* — Boarding pass / ticket (bottom right, ties to events) — */}
      <g transform="translate(214, 128)">
        <rect x="0" y="0" width="88" height="44" rx="6" fill="#fff" stroke={navy} strokeWidth="1.5" />
        <line x1="24" y1="4" x2="24" y2="40" stroke={navy} strokeWidth="1.2" strokeDasharray="3 3" />
        <rect x="6" y="10" width="12" height="8" rx="1" fill="#e9ecef" />
        <rect x="6" y="22" width="14" height="3" rx="1" fill="#dee2e6" />
        <rect x="6" y="28" width="10" height="3" rx="1" fill="#dee2e6" />
        <path
          d="M 44 14 L 56 20 L 44 26 L 50 30 L 40 34 Z"
          fill={navy}
        />
        <rect x="34" y="32" width="46" height="2" rx="1" fill="#dee2e6" />
      </g>
    </svg>
  );
}

function formatChildRegistrantTitle(c) {
  const fn = (c.first_name || "").trim();
  const ln = (c.last_name || "").trim();
  const name = [fn, ln].filter(Boolean).join(" ");
  const base = name || "—";
  // Avoid duplicating the UI's "Child N" labels in compact cards.
  return base;
}

/** Signed-in self registration — collapsed summary (Event Calendar). */
function formatSelfDisplayName(form) {
  const parts = [(form.first_name || "").trim(), (form.last_name || "").trim()].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

const EC_GATEWAY_CHOICE_PILL_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 999,
  padding: "14px 16px",
  fontSize: "14px",
  lineHeight: 1.2,
  fontWeight: 700,
  cursor: "pointer",
  color: "#fff",
  background: "var(--app-primary)",
  border: "1px solid var(--app-primary)",
};

function getFormPrefillFromUser(info) {
  if (!info) return null;
  const first = (info.first_name ?? "").trim();
  const last = (info.last_name != null ? String(info.last_name).trim() : "");
  let birthdayStr = "";
  if (info.birthday != null && info.birthday !== "") {
    birthdayStr = getBirthMonthYearInputValue(info.birthday);
  }
  const pName = (info.parent_name || "").trim();
  const pParts = pName.split(/\s+/).filter(Boolean);
  return {
    first_name: first,
    last_name: last,
    email: (info.email_id || "").trim(),
    phone: formatUsPhoneAsYouType((info.phone_number || "").trim()),
    birthday: birthdayStr,
    gender: (info.gender || "").trim(),
    parent_first_name: pParts[0] || "",
    parent_last_name: pParts.slice(1).join(" ") || "",
    parent_email: (info.parent_email || "").trim(),
    parent_phone: formatUsPhoneAsYouType((info.parent_phone || "").trim()),
    grade: mapGradeValue(info.grade || ""),
    school: (info.school || "").trim(),
  };
}

const EMPTY_ATTEND_REGISTRATION_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  birthday: "",
  gender: "",
  parent_first_name: "",
  parent_last_name: "",
  parent_email: "",
  parent_phone: "",
  parent_preferred_name: "",
  parent_gender: "",
  parent_zip_code: "",
  parent_highest_education_level: "",
  parent_hear_about_us: "",
  parent_hear_about_us_other: "",
  grade: "",
  school: "",
};

/** Prefill parent/guardian contact from the signed-in account (parent registration API fields). */
function getParentFormPrefillFromUser(info) {
  if (!info) return null;
  return {
    ...EMPTY_ATTEND_REGISTRATION_FORM,
    parent_first_name: (info.first_name ?? "").trim(),
    parent_last_name: (info.last_name != null ? String(info.last_name).trim() : ""),
    parent_email: (info.email_id || "").trim(),
    parent_phone: formatUsPhoneAsYouType((info.phone_number || "").trim()),
    parent_preferred_name: (info.preferred_name || "").trim(),
    parent_gender: (info.gender || "").trim(),
    parent_zip_code: (info.zip_code || "").trim(),
    parent_highest_education_level: mapEducationLevelValue(info.highest_education_level || ""),
    parent_hear_about_us: mapHearAboutUsValue(info.hear_about_us || ""),
    parent_hear_about_us_other: (info.hear_about_us_other || "").trim(),
  };
}

const emptyChild = () => ({
  id: Math.random().toString(36).slice(2),
  attend: true,
  linked_user_id: "",
  already_registered_for_event: false,
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  birthday: "",
  gender: "",
  grade: "",
  school: "",
  preferred_name: "",
  school_district: "",
  race_ethnicity: [],
  race_ethnicity_other: "",
  photography_consent: null,
});

/** Map a User document from /getLinkedChildrenForParent into a parent-flow child row. */
function userDocToChildRow(u) {
  const base = emptyChild();
  const birthdayStr = getBirthMonthYearInputValue(u?.birthday);
  return {
    ...base,
    attend: true,
    linked_user_id: u && u._id != null ? String(u._id) : "",
    already_registered_for_event: false,
    first_name: u && u.first_name != null ? String(u.first_name).trim() : "",
    last_name: u && u.last_name != null ? String(u.last_name).trim() : "",
    email: u && u.email_id != null ? String(u.email_id).trim() : "",
    phone: u ? formatUsPhoneAsYouType(String(u.phone_number || "").trim()) : "",
    birthday: birthdayStr,
    gender: u && u.gender != null ? String(u.gender).trim() : "",
    grade: u ? mapGradeValue(u.grade || "") : "",
    school: u && u.school != null ? String(u.school).trim() : "",
    preferred_name: u && u.preferred_name != null ? String(u.preferred_name).trim() : "",
    school_district: u && u.school_district != null ? String(u.school_district).trim() : "",
    race_ethnicity: Array.isArray(u?.race_ethnicity) ? u.race_ethnicity : [],
    race_ethnicity_other: u && u.race_ethnicity_other != null ? String(u.race_ethnicity_other).trim() : "",
  };
}

/** @param {Map<string, Set<string>>} registeredParticipantsByEventId */
function childAlreadyRegisteredForEvent(child, eventId, registeredParticipantsByEventId) {
  const uid = (child?.linked_user_id || "").trim();
  if (!uid) return false;
  const set = registeredParticipantsByEventId.get(String(eventId));
  return !!(set && set.has(uid));
}

/** @param {Map<string, Set<string>>} registeredParticipantsByEventId */
function countBulkParentRegistrationSelections(children, bulkEvents, registeredParticipantsByEventId) {
  let n = 0;
  for (const ev of bulkEvents || []) {
    const eid = String(ev._id);
    for (const c of children || []) {
      const m = c?.bulk_event_attend || {};
      if (!m[eid]) continue;
      if (!bulkParentCellIsToggleable(c, ev, registeredParticipantsByEventId)) continue;
      n += 1;
    }
  }
  return n;
}

function bulkParentCellIsToggleable(child, ev, registeredParticipantsByEventId) {
  if (!child || !ev) return false;
  if (child.is_parent_self && !parentIsRegistrableAttendeeForEvent(ev)) return false;
  return !childAlreadyRegisteredForEvent(child, String(ev._id), registeredParticipantsByEventId);
}

/** True when every non–already-registered attendee×event cell is checked (and at least one such cell exists). */
function bulkParentAllToggleableCellsChecked(children, bulkEvents, registeredParticipantsByEventId) {
  let sawToggleable = false;
  for (const c of children || []) {
    if (!c) continue;
    const m = c.bulk_event_attend || {};
    for (const ev of bulkEvents || []) {
      const eid = String(ev._id);
      if (!bulkParentCellIsToggleable(c, ev, registeredParticipantsByEventId)) continue;
      sawToggleable = true;
      if (!m[eid]) return false;
    }
  }
  return sawToggleable;
}

/** True if there is at least one checkbox the parent can change (not already registered for that event). */
function bulkParentHasAnyToggleableCell(children, bulkEvents, registeredParticipantsByEventId) {
  for (const c of children || []) {
    if (!c) continue;
    for (const ev of bulkEvents || []) {
      if (bulkParentCellIsToggleable(c, ev, registeredParticipantsByEventId)) return true;
    }
  }
  return false;
}

/**
 * User ids used to decide if an event still belongs on "Available" for this account.
 * Youth Only + Parent: only linked children (parents are not event participants for these rows).
 * All Ages + Parent: signed-in parent plus linked children.
 * Otherwise: signed-in user (plus linked children when Parent).
 */
function getUserIdsForEventAvailabilityCheck(ev, userInfo, linkedChildUserIds) {
  if (!userInfo?._id) return new Set();
  const self = String(userInfo._id);
  if (isYouthOnlyEvent(ev) && userInfoHasParentRole(userInfo)) {
    const kids = (linkedChildUserIds || []).map((id) => String(id)).filter(Boolean);
    if (kids.length > 0) return new Set(kids);
  }
  const ids = new Set([self]);
  if (userInfoHasParentRole(userInfo)) {
    for (const cid of linkedChildUserIds || []) {
      if (cid) ids.add(String(cid));
    }
  }
  return ids;
}

/** Map signed-in Parent into a selectable attendee row for All Ages registration. */
function userDocToParentSelfRow(info, registeredUserIdsForThisEvent = null) {
  const uid = info && info._id != null ? String(info._id) : "";
  const already =
    registeredUserIdsForThisEvent instanceof Set && !!uid && registeredUserIdsForThisEvent.has(uid);
  return {
    ...emptyChild(),
    id: `parent-self-${uid || "me"}`,
    is_parent_self: true,
    attend: !already,
    linked_user_id: uid,
    already_registered_for_event: already,
    first_name: info && info.first_name != null ? String(info.first_name).trim() : "",
    last_name: info && info.last_name != null ? String(info.last_name).trim() : "",
    email: info && info.email_id != null ? String(info.email_id).trim() : "",
    phone: info ? formatUsPhoneAsYouType(String(info.phone_number || "").trim()) : "",
    birthday: "",
    gender: info && info.gender != null ? String(info.gender).trim() : "",
    grade: "",
    school: "",
  };
}

function withParentSelfAttendeeRow(childRows, userInfo, eventsForContext, registeredUserIdsForThisEvent = null) {
  const kids = Array.isArray(childRows) ? childRows : [];
  if (!shouldIncludeParentSelfAttendee(eventsForContext) || !userInfo?._id) return kids;
  if (kids.some((c) => c?.is_parent_self)) return kids;
  return [userDocToParentSelfRow(userInfo, registeredUserIdsForThisEvent), ...kids];
}

/**
 * Labeled form field for Event Calendar signup/registration forms.
 * Must stay at module scope — defining this inside EventCalendar remounts
 * inputs on every keystroke and steals focus after one character.
 */
function EcFieldBlock({ label, required, inputId, children, fullWidth = false }) {
  return (
    <div
      className="form-field"
      style={{ minWidth: 0, ...(fullWidth ? { gridColumn: "1 / -1" } : {}) }}
    >
      <label htmlFor={inputId}>
        {label}
        {required ? " *" : ""}
      </label>
      {children}
    </div>
  );
}

function EventCalendar() {
  const { user, userInfo, setUserInfo, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewVolunteerOpportunities =
    hasPermission && hasPermission("menu.volunteer_opportunities");
  const canViewVolunteerSignup = hasPermission && hasPermission("menu.volunteer_signup");
  const handleVolunteerOpportunitiesTabClick = useCallback(() => {
    if (canViewVolunteerOpportunities) {
      navigate("/VolunteerOpportunities");
      return;
    }
    if (canViewVolunteerSignup) {
      navigate("/VolunteerSignup");
      return;
    }
    setActiveTab("volunteer-opportunities");
  }, [canViewVolunteerOpportunities, canViewVolunteerSignup, navigate]);
  // useNavigate previously used for login redirects; gateway modal now handles auth in-place.
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const publicCalendarAccountId = uiConfig.publicCalendarAccountId || null;

  const subdomain = getCurrentSubdomain();
  const [subdomainAccountId, setSubdomainAccountId] = useState(null);
  const [subdomainAccountLoading, setSubdomainAccountLoading] = useState(!!subdomain);

  useEffect(() => {
    if (!subdomain) {
      setSubdomainAccountId(null);
      setSubdomainAccountLoading(false);
      return;
    }
    let cancelled = false;
    setSubdomainAccountLoading(true);
    fetch(serverUrl + "/account/by-site?site_name=" + encodeURIComponent(subdomain))
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success && data.account && data.account._id) {
          setSubdomainAccountId(data.account._id);
        } else {
          setSubdomainAccountId(null);
        }
      })
      .catch(() => {
        if (!cancelled) setSubdomainAccountId(null);
      })
      .finally(() => {
        if (!cancelled) setSubdomainAccountLoading(false);
      });
    return () => { cancelled = true; };
  }, [subdomain, serverUrl]);

  // Priority: subdomain account > URL param > logged-in user's account > public calendar account
  const account_id =
    subdomainAccountId ||
    searchParams.get("account_id") ||
    userInfo?.account_id ||
    publicCalendarAccountId;

  const [eventSignupMode, setEventSignupMode] = useState(DEFAULT_EVENT_SIGNUP_MODE);
  const [accountDisplayName, setAccountDisplayName] = useState("");
  const [eventSignupSettingsLoading, setEventSignupSettingsLoading] = useState(false);

  const allowStudentYouthSignup = useMemo(
    () => allowsStudentYouthSignup(eventSignupMode),
    [eventSignupMode]
  );
  const allowFamilyParentSignup = useMemo(
    () => allowsFamilyParentSignup(eventSignupMode),
    [eventSignupMode]
  );
  const signupOptionsAvailable = useMemo(
    () => eventSignupModeAllowsAnySignup(eventSignupMode),
    [eventSignupMode]
  );
  const signupChoiceSubtitle = useMemo(() => {
    if (allowStudentYouthSignup && allowFamilyParentSignup) {
      return "How would you like to sign up?";
    }
    const name = (accountDisplayName || "").trim();
    return name ? `Welcome to ${name} !` : "Welcome!";
  }, [allowStudentYouthSignup, allowFamilyParentSignup, accountDisplayName]);

  useEffect(() => {
    if (!account_id) {
      setEventSignupMode(DEFAULT_EVENT_SIGNUP_MODE);
      setAccountDisplayName("");
      setEventSignupSettingsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setEventSignupSettingsLoading(true);
    fetch(`${serverUrl}/account/public-settings?account_id=${encodeURIComponent(account_id)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.success && data.account) {
          setEventSignupMode(normalizeAccountEventSignupMode(data.account.event_signup_mode));
          setAccountDisplayName(
            data.account.full_name != null ? String(data.account.full_name).trim() : ""
          );
        } else {
          setEventSignupMode(DEFAULT_EVENT_SIGNUP_MODE);
          setAccountDisplayName("");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEventSignupMode(DEFAULT_EVENT_SIGNUP_MODE);
          setAccountDisplayName("");
        }
      })
      .finally(() => {
        if (!cancelled) setEventSignupSettingsLoading(false);
      });
    return () => { cancelled = true; };
  }, [account_id, serverUrl]);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myRegisteredEvents, setMyRegisteredEvents] = useState([]);
  /** Linked child user ids for the signed-in parent (same account). Used with getMyEvents rows for per-child availability. */
  const [linkedChildUserIds, setLinkedChildUserIds] = useState([]);
  /** Linked child User docs (same source as linkedChildUserIds) for My Check-In QR Codes tab. */
  const [linkedChildrenUsers, setLinkedChildrenUsers] = useState([]);
  const [registrationFamilyContextLoading, setRegistrationFamilyContextLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("available"); // "available" | "registered" | "participant-qr" | "volunteer-opportunities"
  const [programFilterId, setProgramFilterId] = useState(PROGRAM_FILTER_ALL);
  const [programs, setPrograms] = useState([]);
  /** Free-text search across the active tab’s list. */
  const [eventListSearchTerm, setEventListSearchTerm] = useState("");
  /** My Registered Events: Open / Expired / Closed (same semantics as Events). Default Open. */
  const [registeredStatusFilter, setRegisteredStatusFilter] = useState("open"); // 'all' | 'open' | 'expired' | 'closed'
  const [eventListViewMode, setEventListViewMode] = useState("card");
  const eventListTimeNav = useGlobalTimeNavigator({ initialView: "month" });
  const { sortConfig: eventListSortConfig, handleSort: handleEventListSort } = useTableSort(
    EVENT_CALENDAR_LIST_SORT_INITIAL,
    activeTab
  );
  const [selectedEventIds, setSelectedEventIds] = useState(() => new Set());
  const [bannerError, setBannerError] = useState("");
  const [modalEvent, setModalEvent] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkEvents, setBulkEvents] = useState([]);
  // Account-first registration: do not offer guest registration paths.
  // Keep modal in "guest-form" mode for the existing signed-in self/parent flows.
  const [attendStep, setAttendStep] = useState("guest-form"); // "gateway" | "guest-form"
  const [guestMode, setGuestMode] = useState("self"); // "self" | "parent"
  // Gateway matches existing platform pattern: separate buttons for returning users, new self, new parent.
  const [gatewayMode, setGatewayMode] = useState("choice"); // "choice" | "signup-self" | "signup-parent"
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [coppaPrivacyConsent, setCoppaPrivacyConsent] = useState(false);
  /** Optional SMS opt-in (unchecked by default; Twilio affirmative consent). */
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [profileSignupOpen, setProfileSignupOpen] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState({ checking: false, inUse: false, message: "" });
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    birthday: "",
    gender: "",
    parent_first_name: "",
    parent_last_name: "",
    parent_email: "",
    parent_phone: "",
    parent_preferred_name: "",
    parent_gender: "",
    parent_zip_code: "",
    parent_highest_education_level: "",
    parent_hear_about_us: "",
    parent_hear_about_us_other: "",
    grade: "",
    school: "",
  });
  const [children, setChildren] = useState(() => [emptyChild()]);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingParticipantId, setCancellingParticipantId] = useState(null);
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: "",
    message: "",
    contentMaxWidth: null,
    confirmLabel: null,
    paymentLinkUrls: null,
  });
  const paymentSuccessEventName = searchParams.get("payment_success") === "1" ? searchParams.get("event_name") || "" : null;
  const lastFocusedInputIdRef = useRef("");
  const lastFocusedSelectionRef = useRef({ id: "", start: null, end: null, direction: "none", value: "" });
  const modalContentRef = useRef(null);
  const lastModalTypingAtRef = useRef(0);
  const pendingAfterAuthRef = useRef(null); // { type: "self-single" | "self-bulk" | "parent-single" | "parent-bulk", event?: object, events?: object[] }

  // Keep focus stable while typing in the registration modal.
  // Root cause is a re-mount somewhere in this modal subtree; this prevents the UX breakage.
  // Do not restore focus onto <select> (or non-text controls): on iOS that re-opens the native picker
  // after choosing a value (e.g. Grade), so the selection appears to "stay open" on mobile only.
  useLayoutEffect(() => {
    const modalOpen = !!(modalEvent || bulkMode || profileSignupOpen);
    // Apply the same focus-stabilizer to both the profile signup and the registration form.
    if (!modalOpen || (!profileSignupOpen && attendStep !== "guest-form")) return;
    const modalEl = modalContentRef.current;
    if (typeof document === "undefined") return;
    // Only intervene when focus has actually dropped outside the modal (the real bug).
    if (modalEl && document.activeElement && modalEl.contains(document.activeElement)) return;
    const active = document.activeElement;
    const focusDropped =
      !active ||
      active === document.body ||
      (modalEl && !modalEl.contains(active));
    if (!focusDropped) return;
    // Avoid unexpected refocus long after typing; only repair shortly after input.
    const msSinceType = Date.now() - (lastModalTypingAtRef.current || 0);
    if (msSinceType > 900) return;
    const id = (lastFocusedInputIdRef.current || "").trim();
    if (!id) return;
    const el = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!el || document.activeElement === el) return;
    const tag = (el.tagName || "").toUpperCase();
    if (tag === "SELECT" || tag === "BUTTON") return;
    if (tag === "INPUT") {
      const type = (el.getAttribute("type") || "text").toLowerCase();
      if (["button", "submit", "reset", "checkbox", "radio", "file", "hidden", "image", "range", "color"].includes(type)) {
        return;
      }
    } else if (tag !== "TEXTAREA") {
      return;
    }
    try {
      el.focus({ preventScroll: true });
      // Preserve caret position (prevents "cursor jumps to end" while typing).
      const sel = lastFocusedSelectionRef.current;
      if (sel && sel.id === id && typeof el.setSelectionRange === "function") {
        const start = typeof sel.start === "number" ? sel.start : null;
        const end = typeof sel.end === "number" ? sel.end : null;
        // For plain text fields like email/password, React updates can reset caret to end.
        // Force restoring caret for email/password; other fields only when value is unchanged.
        const forceCaretRestore = /email|signup-password/i.test(id);
        const valueUnchanged = String(el.value || "") === String(sel.value || "");
        if (start != null && end != null && (forceCaretRestore || valueUnchanged)) {
          const max = String(el.value || "").length;
          el.setSelectionRange(Math.min(start, max), Math.min(end, max), sel.direction || "none");
        }
      }
    } catch {
      // ignore
    }
  }, [form, children, signupPassword, signupConfirmPassword, modalEvent, bulkMode, profileSignupOpen, attendStep, gatewayMode]);

  const selfFlowYouthOnlyContext = useMemo(
    () =>
      (!!modalEvent && !bulkMode && isYouthOnlyEvent(modalEvent)) ||
      (bulkMode && bulkEvents.length > 0 && bulkEvents.some(isYouthOnlyEvent)),
    [bulkMode, modalEvent, bulkEvents]
  );

  // After authentication completes, continue the pending registration action.
  useEffect(() => {
    if (!userInfo?._id) return;
    const pending = pendingAfterAuthRef.current;
    if (!pending) return;
    pendingAfterAuthRef.current = null;

    if (pending.type === "self-single" && pending.event) {
      // After login from a direct event/QR link: open the registration modal; do not auto-register.
      setModalEvent(pending.event);
      setBulkMode(false);
      setBulkEvents([]);
      setAttendStep("guest-form");
      if (shouldOpenParentRegistrationFlow(userInfo, pending.event)) {
        setGuestMode("parent");
        setForm(getParentFormPrefillFromUser(userInfo) || { ...EMPTY_ATTEND_REGISTRATION_FORM });
        setChildren([]);
        // Linked attendees loaded by effect when parent modal opens.
      } else {
        setGuestMode("self");
        setChildren([emptyChild()]);
        setForm(getFormPrefillFromUser(userInfo) || EMPTY_ATTEND_REGISTRATION_FORM);
      }
      return;
    }
    if (pending.type === "self-bulk" && Array.isArray(pending.events) && pending.events.length > 0) {
      // Keep the bulk modal open and let the existing bulk form handler use signed-in endpoint.
      setAttendStep("guest-form");
      if (shouldOpenParentRegistrationFlow(userInfo, pending.events)) {
        setGuestMode("parent");
        setBulkMode(true);
        setBulkEvents(pending.events);
        setForm(getParentFormPrefillFromUser(userInfo) || { ...EMPTY_ATTEND_REGISTRATION_FORM });
        setChildren([]);
      } else {
        setGuestMode("self");
        setForm(getFormPrefillFromUser(userInfo) || EMPTY_ATTEND_REGISTRATION_FORM);
      }
      return;
    }
    if ((pending.type === "parent-single" || pending.type === "parent-bulk") && (pending.event || pending.events)) {
      setAttendStep("guest-form");
      setGuestMode("parent");
      setForm(getParentFormPrefillFromUser(userInfo) || { ...EMPTY_ATTEND_REGISTRATION_FORM });
      // Linked attendees loaded by effect when parent modal opens.
    }
  }, [userInfo]);

  function openAlert(message, title = ALERT_TITLE_CHECK_DETAILS) {
    setAlertDialog({
      open: true,
      title,
      message: message || "",
      contentMaxWidth: null,
      confirmLabel: null,
      paymentLinkUrls: null,
    });
  }

  function openRegistrationOutcomeAlert({ confirmed, waitlisted, errors, paymentLinkUrls }) {
    const links = Array.isArray(paymentLinkUrls)
      ? [...new Set(paymentLinkUrls.map((u) => String(u || "").trim()).filter(Boolean))]
      : [];
    setAlertDialog({
      open: true,
      title: "Registration confirmation",
      message: (
        <>
          <EventRegistrationConfirmationBody
            confirmed={confirmed}
            waitlisted={waitlisted}
            errors={errors}
          />
          {links.length > 1 && (
            <div style={{ marginTop: 16 }}>
              <strong>Payments required</strong>
              <div>Open each payment page in this tab:</div>
              <ul style={{ marginBottom: 0 }}>
                {links.map((url, index) => (
                  <li key={url}>
                    <a href={url} target="_blank" rel="noopener noreferrer">Pay registration {index + 1}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ),
      contentMaxWidth: 560,
      confirmLabel: links.length === 1 ? "Pay now" : null,
      paymentLinkUrls: links.length === 1 ? links : null,
    });
  }

  function closeAlertDialog() {
    setAlertDialog({
      open: false,
      title: "",
      message: "",
      contentMaxWidth: null,
      confirmLabel: null,
      paymentLinkUrls: null,
    });
  }

  function handleAlertConfirm() {
    const links = Array.isArray(alertDialog.paymentLinkUrls) ? alertDialog.paymentLinkUrls : [];
    if (links.length > 0) {
      closeAlertDialog();
      links.forEach((url) => window.open(url, "_blank", "noopener,noreferrer"));
      return;
    }
    closeAlertDialog();
  }

  /** When API returns counts but no per-row results, show a minimal summary row. */
  function mergeParentOutcomeFallbackRows(confirmed, waitlisted, created, waitlistedCount, defaultEventName) {
    const ev = (defaultEventName || "Event").trim();
    const c = [...(confirmed || [])];
    const w = [...(waitlisted || [])];
    if (c.length === 0 && w.length === 0 && (created > 0 || waitlistedCount > 0)) {
      if (created > 0) {
        c.push({
          participantLabel: "Your registrants",
          eventName: ev,
          status: "registered",
          detail: `${created} registration(s) confirmed.`,
        });
      }
      if (waitlistedCount > 0) {
        w.push({
          participantLabel: "Your registrants",
          eventName: ev,
          status: "waitlisted",
          detail: `${waitlistedCount} waitlist registration(s).`,
        });
      }
    }
    return { confirmed: c, waitlisted: w };
  }

  function getFriendlySignupError(err) {
    const code = (err && typeof err === "object" && err.code != null) ? String(err.code) : "";
    if (code === "auth/email-already-in-use") {
      return "This email is already in use. Please use a different email.";
    }
    if (code === "auth/invalid-email") {
      return "Please enter a valid email address.";
    }
    if (code === "auth/weak-password") {
      return "Password must be at least 6 characters.";
    }
    if (code === "auth/operation-not-allowed") {
      return "Sign up is not available right now. Please try again later.";
    }
    // Fallback: strip "Firebase:" prefix when present.
    const raw = (err && (err.message || err.toString)) ? (err.message || String(err)) : "";
    const msg = String(raw || "").replace(/^Firebase:\s*/i, "").trim();
    return msg || "Could not create account.";
  }

  function openValidationErrors(errors, title = ALERT_TITLE_ADD_REQUIRED_INFO) {
    const msg = formatValidationErrorList(errors);
    if (!msg) return false;
    openAlert(msg, title);
    return true;
  }

  async function handleGatewayCreateAccount() {
    if (submitting) return;
    const isParent = gatewayMode === "signup-parent";
    if (isParent && !allowFamilyParentSignup) {
      openAlert("This organization only allows Student / Youth account creation.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }
    if (!isParent && !allowStudentYouthSignup) {
      openAlert("This organization only allows Family / Parent account creation.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }
    const first = (isParent ? form.parent_first_name : form.first_name || "").trim();
    const last = (isParent ? form.parent_last_name : form.last_name || "").trim();
    const email = (isParent ? form.parent_email : form.email || "").trim();
    const phone = (isParent ? form.parent_phone : form.phone || "").trim();
    const pw = signupPassword;
    const pw2 = signupConfirmPassword;

    // Collect all field errors so the user sees everything after one submit.
    const errors = [];
    if (!first || !last) {
      errors.push("First name and last name are required.");
    }
    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) {
      errors.push(emailCheck.message || "Invalid email.");
    }
    const phoneCheck = validatePhone(phone);
    if (!phoneCheck.valid) {
      errors.push(phoneCheck.message || "Invalid phone.");
    }
    if (!pw || pw.length < 6) {
      errors.push("Password must be at least 6 characters.");
    }
    if (pw !== pw2) {
      errors.push("Passwords do not match.");
    }

    // Parent signup: required child participant details must match UI asterisks.
    if (isParent) {
      const genderErr = validateRequiredGender(form.parent_gender);
      if (genderErr) errors.push(genderErr);
      // Parent contact + children (incl. zip/edu/hear) — collect all, not first-only.
      // Skip parent name/email/phone here; already validated above for the account email.
      errors.push(
        ...validateParentRegistrationInputs({
          eventsForContext: [{ audience_type: "Youth Only" }],
          skipParentContact: true,
        })
      );
      if (!coppaPrivacyConsent) {
        errors.push("Please read and accept the privacy statement to continue.");
      }
      const attending = (children || []).filter((c) => !!c?.attend);
      for (let i = 0; i < attending.length; i++) {
        const photoErr = validateRequiredPhotographyConsent(attending[i].photography_consent);
        if (photoErr) errors.push(`Child ${i + 1}: ${photoErr}`);
      }
    }

    // Self signup: collect Youth + Parent/Guardian info as mandatory profile fields.
    const isYouthContext = !isParent;
    let youthPayload = null;
    let parentPayload = null;
    if (isYouthContext) {
      const dobErr = validateRequiredBirthMonthYear(form.birthday);
      if (dobErr) errors.push(dobErr);
      const birthdayNorm = normalizeBirthMonthYearForSave((form.birthday || "").trim());
      const genderErr = validateRequiredGender(form.gender);
      if (genderErr) errors.push(genderErr);
      const gradeVal = mapGradeValue((form.grade || "").trim());
      if (!gradeVal) errors.push("Please select a valid grade option.");
      if (!(form.school || "").trim()) errors.push("School is required.");
      if (!(form.parent_first_name || "").trim() || !(form.parent_last_name || "").trim()) {
        errors.push("Parent/guardian first and last name are required.");
      }
      if (!(form.parent_email || "").trim()) {
        errors.push("Parent/guardian email is required.");
      } else {
        const parentEmailCheck = validateEmail(form.parent_email);
        if (!parentEmailCheck.valid) {
          errors.push(parentEmailCheck.message || "Invalid parent/guardian email.");
        }
      }
      if (!(form.parent_phone || "").trim()) {
        errors.push("Parent/guardian phone is required.");
      } else {
        const parentPhoneCheck = validatePhone(form.parent_phone);
        if (!parentPhoneCheck.valid) {
          errors.push(parentPhoneCheck.message || "Invalid parent/guardian phone.");
        }
      }
      if (errors.length === 0) {
        youthPayload = {
          birthday: birthdayNorm.storageYmd,
          gender: (form.gender || "").trim(),
          grade: gradeVal,
          school: (form.school || "").trim(),
        };
        parentPayload = {
          first_name: (form.parent_first_name || "").trim(),
          last_name: (form.parent_last_name || "").trim(),
          email: (form.parent_email || "").trim(),
          phone: (form.parent_phone || "").trim(),
        };
      }
    }

    if (openValidationErrors(errors)) return;

    // Email-in-use validation should run on submit (not while typing) to avoid flicker.
    setEmailCheckStatus({ checking: true, inUse: false, message: "" });
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      const list = Array.isArray(methods) ? methods : [];
      const inUse = list.length > 0;
      if (inUse) {
        setEmailCheckStatus({
          checking: false,
          inUse: true,
          message: "This email is already in use. Please sign in instead.",
        });
        openAlert("This email is already in use. Please sign in instead.", "Email already in use");
        return;
      }
      setEmailCheckStatus({ checking: false, inUse: false, message: "" });
    } catch {
      // If Firebase lookup fails, don't block signup; treat as unknown and proceed.
      setEmailCheckStatus({ checking: false, inUse: false, message: "" });
    }

    const eventAccountId = (modalEvent && modalEvent.account_id) ? modalEvent.account_id : account_id;
    if (!eventAccountId) {
      openAlert("Unable to create account: account is missing.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }

    setSubmitting(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, pw);
      const firebaseUser = cred?.user;
      if (!firebaseUser) throw new Error("Could not create Firebase account.");
      const idToken = await firebaseUser.getIdToken(true);
      const resp = await apiFetch(`${serverInfo.serverUrl}/signup/firebase`, {
        method: "POST",
        body: JSON.stringify({
          idToken,
          account_id: eventAccountId,
          first_name: first,
          last_name: last,
          phone_number: phone,
          role: isParent ? "Parent" : "Participant",
          sms_opt_in: !!smsOptIn,
        }),
      });
      const text = await resp.text().catch(() => "");
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!resp.ok) {
        throw new Error(data?.message || "Could not create profile.");
      }
      const createdInfo = data?.userInfo || null;
      if (createdInfo) {
        setUserInfo(createdInfo);
      }

      // Parent signup: create/link child profiles entered in the signup modal.
      if (createdInfo && isParent) {
        const parentUserId = String(createdInfo._id || "").trim();
        const parentEmail = (createdInfo.email_id || email || "").trim();
        if (!parentUserId) {
          throw new Error("Could not create linked children: parent profile is missing.");
        }
        const kidsToCreate = (children || []).filter((c) => !!c?.attend);
        const createdKids = [];
        for (const c of kidsToCreate) {
          const birthdayNorm = normalizeBirthMonthYearForSave((c.birthday || "").trim());
          if (!birthdayNorm.ok) {
            throw new Error(birthdayNorm.error);
          }
          const respKid = await apiFetch(`${serverInfo.serverUrl}/createLinkedChildForParent`, {
            method: "POST",
            body: JSON.stringify({
              account_id: eventAccountId,
              parent_user_id: parentUserId,
              parent_email: parentEmail,
              child: {
                first_name: (c.first_name || "").trim(),
                last_name: (c.last_name || "").trim(),
                birthday: birthdayNorm.storageYmd || undefined,
                gender: (c.gender || "").trim(),
                grade: mapGradeValue(c.grade || "") || undefined,
                school: (c.school || "").trim() || undefined,
                ...buildChildProfilePayload(c),
              },
            }),
          });
          if (!respKid.ok) {
            const t = await respKid.text().catch(() => "");
            throw new Error(t || "Could not create linked child.");
          }
          const kidData = await respKid.json().catch(() => ({}));
          if (kidData?.child) createdKids.push(kidData.child);
        }
        if (createdKids.length > 0) {
          setChildren(createdKids.map(userDocToChildRow));
        }

        const parentProfilePatch = {
          preferred_name: (form.parent_preferred_name || "").trim(),
          gender: (form.parent_gender || "").trim(),
          zip_code: (form.parent_zip_code || "").trim(),
          highest_education_level: mapEducationLevelValue(form.parent_highest_education_level || ""),
          hear_about_us: mapHearAboutUsValue(form.parent_hear_about_us || ""),
          hear_about_us_other:
            mapHearAboutUsValue(form.parent_hear_about_us || "") === HEAR_ABOUT_US_OTHER_VALUE
              ? (form.parent_hear_about_us_other || "").trim()
              : "",
        };
        const updParent = await apiFetch(`${serverInfo.serverUrl}/updateUser`, {
          method: "POST",
          body: JSON.stringify({
            _id: parentUserId,
            account_id: String(createdInfo.account_id),
            ...parentProfilePatch,
          }),
        });
        if (!updParent.ok) {
          const t = await updParent.text().catch(() => "");
          throw new Error(t || "Could not save parent profile details.");
        }
        setUserInfo((prev) => (prev ? { ...prev, ...parentProfilePatch } : prev));
      }

      // Persist additional Youth-only fields + parent contact immediately so registration works with no extra steps.
      if (createdInfo && isYouthContext && youthPayload && parentPayload) {
        // Save youth fields onto the User profile.
        const upd = await apiFetch(`${serverInfo.serverUrl}/updateUser`, {
          method: "POST",
          body: JSON.stringify({
            _id: String(createdInfo._id),
            account_id: String(createdInfo.account_id),
            birthday: youthPayload.birthday,
            gender: youthPayload.gender,
            grade: youthPayload.grade,
            school: youthPayload.school,
          }),
        });
        if (!upd.ok) {
          const t = await upd.text().catch(() => "");
          throw new Error(t || "Could not save Youth Only profile details.");
        }

        // Save parent/guardian contact (creates/links Parent role user + sets parent_user_id).
        const saveParent = await apiFetch(`${serverInfo.serverUrl}/saveYouthParentContact`, {
          method: "POST",
          body: JSON.stringify({
            account_id: String(createdInfo.account_id),
            user_id: String(createdInfo._id),
            parent: parentPayload,
          }),
        });
        if (!saveParent.ok) {
          const t = await saveParent.text().catch(() => "");
          throw new Error(t || "Could not save parent/guardian contact.");
        }

        // Refresh userInfo so checks see parent_user_id and other fields.
        const refreshedToken = await firebaseUser.getIdToken(true);
        const loginResp = await apiFetch(`${serverInfo.serverUrl}/login/firebase`, {
          method: "POST",
          body: JSON.stringify({ idToken: refreshedToken }),
        });
        if (loginResp.ok) {
          const loginData = await loginResp.json().catch(() => ({}));
          if (loginData?.userInfo) setUserInfo(loginData.userInfo);
        }
      }
      // Profile created; close signup modal and let the existing "pendingAfterAuth" effect continue the journey.
      closeProfileSignup();
      setGuestMode(isParent ? "parent" : "self");
    } catch (err) {
      openAlert(getFriendlySignupError(err), "Create account failed");
    } finally {
      setSubmitting(false);
    }
  }

  function renderParentContactFieldRows() {
    return (
      <>
        <div className="form-row">
          <EcFieldBlock
            label="First Name"
            required
            inputId="ec-parent-first-parentflow"
          >
            <input
              id="ec-parent-first-parentflow"
              type="text"
              value={form.parent_first_name}
              onChange={(e) => handleFormChange("parent_first_name", e.target.value)}
              required
              autoComplete="off"
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Last Name"
            required
            inputId="ec-parent-last-parentflow"
          >
            <input
              id="ec-parent-last-parentflow"
              type="text"
              value={form.parent_last_name}
              onChange={(e) => handleFormChange("parent_last_name", e.target.value)}
              required
              autoComplete="off"
            />
          </EcFieldBlock>
        </div>
        <div className="form-row">
          <EcFieldBlock
            label="Preferred Name"
            required={false}
            inputId="ec-parent-preferred-parentflow"
          >
            <input
              id="ec-parent-preferred-parentflow"
              type="text"
              value={form.parent_preferred_name}
              onChange={(e) => handleFormChange("parent_preferred_name", e.target.value)}
              autoComplete="off"
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Email"
            required
            inputId="ec-parent-email-parentflow"
          >
            <input
              id="ec-parent-email-parentflow"
              type="email"
              value={form.parent_email}
              onChange={(e) => handleFormChange("parent_email", e.target.value)}
              required
              autoComplete="email"
            />
          </EcFieldBlock>
        </div>
        <div className="form-row">
          <EcFieldBlock
            label="Phone"
            required
            inputId="ec-parent-phone-parentflow"
          >
            <PhoneInput
              id="ec-parent-phone-parentflow"
              value={form.parent_phone}
              onChange={(v) => handleFormChange("parent_phone", v)}
              required
              autoComplete="tel"
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Gender"
            required
            inputId="ec-parent-gender-parentflow"
          >
            <GenderSelect
              id="ec-parent-gender-parentflow"
              value={form.parent_gender}
              onChange={(e) => handleFormChange("parent_gender", e.target.value)}
              required
            />
          </EcFieldBlock>
        </div>
        {gatewayMode === "signup-parent" ? (
          <SmsOptInConsentBlock
            checkboxId="ec-signup-sms-opt-in-parent"
            checked={smsOptIn}
            onChange={setSmsOptIn}
            disabled={submitting}
          />
        ) : null}
        {mapHearAboutUsValue(form.parent_hear_about_us) === HEAR_ABOUT_US_OTHER_VALUE ? (
          <>
            <div className="form-row">
              <EcFieldBlock
                label="Zip Code"
                required
                inputId="ec-parent-zip-parentflow"
                fullWidth
              >
                <input
                  id="ec-parent-zip-parentflow"
                  type="text"
                  value={form.parent_zip_code}
                  onChange={(e) => handleFormChange("parent_zip_code", e.target.value)}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  required
                />
              </EcFieldBlock>
            </div>
            <div className="form-row">
              <EcFieldBlock
                label="How Did You Hear About Us?"
                required
                inputId="ec-parent-hear-parentflow"
              >
                <select
                  id="ec-parent-hear-parentflow"
                  value={form.parent_hear_about_us}
                  onChange={(e) => handleFormChange("parent_hear_about_us", e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {HEAR_ABOUT_US_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </EcFieldBlock>
              <EcFieldBlock
                label="Please specify"
                required
                inputId="ec-parent-hear-other-parentflow"
              >
                <input
                  id="ec-parent-hear-other-parentflow"
                  type="text"
                  value={form.parent_hear_about_us_other}
                  onChange={(e) => handleFormChange("parent_hear_about_us_other", e.target.value)}
                  required
                />
              </EcFieldBlock>
            </div>
          </>
        ) : (
          <div className="form-row">
            <EcFieldBlock
              label="Zip Code"
              required
              inputId="ec-parent-zip-parentflow"
            >
              <input
                id="ec-parent-zip-parentflow"
                type="text"
                value={form.parent_zip_code}
                onChange={(e) => handleFormChange("parent_zip_code", e.target.value)}
                inputMode="numeric"
                autoComplete="postal-code"
                required
              />
            </EcFieldBlock>
            <EcFieldBlock
              label="How Did You Hear About Us?"
              required
              inputId="ec-parent-hear-parentflow"
            >
              <select
                id="ec-parent-hear-parentflow"
                value={form.parent_hear_about_us}
                onChange={(e) => handleFormChange("parent_hear_about_us", e.target.value)}
                required
              >
                <option value="">Select…</option>
                {HEAR_ABOUT_US_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </EcFieldBlock>
          </div>
        )}
        <div className="form-row">
          <EcFieldBlock
            label="Highest Education Level Completed"
            required
            inputId="ec-parent-education-parentflow"
            fullWidth
          >
            <select
              id="ec-parent-education-parentflow"
              value={form.parent_highest_education_level}
              onChange={(e) => handleFormChange("parent_highest_education_level", e.target.value)}
              required
            >
              <option value="">Select…</option>
              {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </EcFieldBlock>
        </div>
      </>
    );
  }

  function renderChildDetailFields(c, youthOnlyContext) {
    return (
      <>
        <div className="form-row" style={{ marginTop: 6 }}>
          <EcFieldBlock
            label="First name"
            required={!!c.attend}
            inputId={`ec-pc-fn-${c.id}`}
          >
            <input
              id={`ec-pc-fn-${c.id}`}
              type="text"
              value={c.first_name}
              onChange={(e) =>
                setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, first_name: e.target.value } : x)))
              }
              required={!!c.attend}
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Last name"
            required={!!c.attend}
            inputId={`ec-pc-ln-${c.id}`}
          >
            <input
              id={`ec-pc-ln-${c.id}`}
              type="text"
              value={c.last_name}
              onChange={(e) =>
                setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, last_name: e.target.value } : x)))
              }
              required={!!c.attend}
            />
          </EcFieldBlock>
        </div>
        <div className="form-row">
          <EcFieldBlock
            label="Preferred Name"
            required={false}
            inputId={`ec-pc-pn-${c.id}`}
          >
            <input
              id={`ec-pc-pn-${c.id}`}
              type="text"
              value={c.preferred_name}
              onChange={(e) =>
                setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, preferred_name: e.target.value } : x)))
              }
            />
          </EcFieldBlock>
          {youthOnlyContext ? (
            <EcFieldBlock
              label="Date of birth"
              required={!!c.attend}
              inputId={`ec-pc-bd-${c.id}`}
            >
              <BirthMonthYearInput
                id={`ec-pc-bd-${c.id}`}
                value={c.birthday}
                onChange={(next) => {
                  setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, birthday: next } : x)));
                }}
                required={!!c.attend}
              />
            </EcFieldBlock>
          ) : (
            <EcFieldBlock
              label="School District"
              required={!!c.attend}
              inputId={`ec-pc-sd-${c.id}`}
            >
              <input
                id={`ec-pc-sd-${c.id}`}
                type="text"
                value={c.school_district}
                onChange={(e) =>
                  setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, school_district: e.target.value } : x)))
                }
                required={!!c.attend}
              />
            </EcFieldBlock>
          )}
        </div>

        {youthOnlyContext ? (
          <>
            <div className="form-row">
              <EcFieldBlock
                label="Gender"
                required={!!c.attend}
                inputId={`ec-pc-g-${c.id}`}
              >
                <GenderSelect
                  id={`ec-pc-g-${c.id}`}
                  value={c.gender}
                  onChange={(e) =>
                    setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, gender: e.target.value } : x)))
                  }
                  required={!!c.attend}
                />
              </EcFieldBlock>
              <EcFieldBlock
                label="Grade"
                required={!!c.attend}
                inputId={`ec-pc-gr-${c.id}`}
              >
                <select
                  id={`ec-pc-gr-${c.id}`}
                  value={c.grade}
                  onChange={(e) =>
                    setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, grade: e.target.value } : x)))
                  }
                  required={!!c.attend}
                >
                  <option value=""></option>
                  {GRADE_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </EcFieldBlock>
            </div>
            <div className="form-row">
              <EcFieldBlock
                label="School"
                required={!!c.attend}
                inputId={`ec-pc-sc-${c.id}`}
              >
                <input
                  id={`ec-pc-sc-${c.id}`}
                  type="text"
                  value={c.school}
                  onChange={(e) =>
                    setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, school: e.target.value } : x)))
                  }
                  required={!!c.attend}
                />
              </EcFieldBlock>
              <EcFieldBlock
                label="School District"
                required={!!c.attend}
                inputId={`ec-pc-sd-${c.id}`}
              >
                <input
                  id={`ec-pc-sd-${c.id}`}
                  type="text"
                  value={c.school_district}
                  onChange={(e) =>
                    setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, school_district: e.target.value } : x)))
                  }
                  required={!!c.attend}
                />
              </EcFieldBlock>
            </div>
          </>
        ) : null}

        <div className="form-row">
          <RaceEthnicityMultiSelect
            idPrefix={`ec-pc-re-${c.id}`}
            value={c.race_ethnicity}
            otherValue={c.race_ethnicity_other}
            onChange={(next) => setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, race_ethnicity: next } : x)))}
            onOtherChange={(next) =>
              setChildren((prev) => prev.map((x) => (x.id === c.id ? { ...x, race_ethnicity_other: next } : x)))
            }
            required={!!c.attend}
          />
        </div>
      </>
    );
  }

  function renderSelfContactFieldRows() {
    return (
      <>
        <div className="form-row">
          <EcFieldBlock
            label="First name"
            required
            inputId="ec-first"
          >
            <input
              id="ec-first"
              type="text"
              value={form.first_name}
              onChange={(e) => handleFormChange("first_name", e.target.value)}
              required
              autoComplete="given-name"
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Last name"
            required
            inputId="ec-last"
          >
            <input
              id="ec-last"
              type="text"
              value={form.last_name}
              onChange={(e) => handleFormChange("last_name", e.target.value)}
              required
              autoComplete="family-name"
            />
          </EcFieldBlock>
        </div>
        <div className="form-row">
          <EcFieldBlock
            label="Email"
            required
            inputId="ec-email"
          >
            <input
              id="ec-email"
              type="email"
              value={form.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              required
              autoComplete="email"
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Phone"
            required
            inputId="ec-phone"
          >
            <PhoneInput
              id="ec-phone"
              value={form.phone}
              onChange={(v) => handleFormChange("phone", v)}
              required
              autoComplete="tel"
            />
          </EcFieldBlock>
        </div>
        {gatewayMode === "signup-self" ? (
          <SmsOptInConsentBlock
            checkboxId="ec-signup-sms-opt-in-self"
            checked={smsOptIn}
            onChange={setSmsOptIn}
            disabled={submitting}
          />
        ) : null}
      </>
    );
  }

  function renderGatewayPasswordFieldRow() {
    return (
      <div className="form-row">
        <EcFieldBlock label="Password" required inputId="ec-signup-password">
          <PasswordInput
            id="ec-signup-password"
            value={signupPassword}
            onChange={(e) => setSignupPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="Create a password"
            disabled={submitting}
            required
          />
        </EcFieldBlock>
        <EcFieldBlock label="Confirm password" required inputId="ec-signup-password2">
          <PasswordInput
            id="ec-signup-password2"
            value={signupConfirmPassword}
            onChange={(e) => setSignupConfirmPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="Confirm password"
            disabled={submitting}
            required
          />
        </EcFieldBlock>
      </div>
    );
  }

  function renderSelfYouthFieldRows(requiredFields = true) {
    return (
      <>
        <div className="form-row">
          <EcFieldBlock
            label="Date of birth"
            required={!!requiredFields}
            inputId="ec-birthday"
          >
            <BirthMonthYearInput
              id="ec-birthday"
              value={form.birthday}
              onChange={(next) => handleFormChange("birthday", next)}
              required={!!requiredFields}
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Gender"
            required={!!requiredFields}
            inputId="ec-gender"
          >
            <GenderSelect
              id="ec-gender"
              value={form.gender}
              onChange={(e) => handleFormChange("gender", e.target.value)}
              required={!!requiredFields}
            />
          </EcFieldBlock>
        </div>
        <div className="form-row">
          <EcFieldBlock
            label="Grade"
            required={!!requiredFields}
            inputId="ec-grade"
          >
            <select
              id="ec-grade"
              value={form.grade}
              onChange={(e) => handleFormChange("grade", e.target.value)}
              required={!!requiredFields}
            >
              <option value=""></option>
              {GRADE_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </EcFieldBlock>
          <EcFieldBlock
            label="School"
            required={!!requiredFields}
            inputId="ec-school"
          >
            <input
              id="ec-school"
              type="text"
              value={form.school}
              onChange={(e) => handleFormChange("school", e.target.value)}
              required={!!requiredFields}
            />
          </EcFieldBlock>
        </div>
      </>
    );
  }

  function renderSelfParentGuardianFieldRows(requiredFields = true) {
    return (
      <>
        <div className="form-row">
          <EcFieldBlock
            label="First Name"
            required={!!requiredFields}
            inputId="ec-parent-first"
          >
            <input
              id="ec-parent-first"
              type="text"
              value={form.parent_first_name}
              onChange={(e) => handleFormChange("parent_first_name", e.target.value)}
              required={!!requiredFields}
              autoComplete="off"
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Last Name"
            required={!!requiredFields}
            inputId="ec-parent-last"
          >
            <input
              id="ec-parent-last"
              type="text"
              value={form.parent_last_name}
              onChange={(e) => handleFormChange("parent_last_name", e.target.value)}
              required={!!requiredFields}
              autoComplete="off"
            />
          </EcFieldBlock>
        </div>
        <div className="form-row">
          <EcFieldBlock
            label="Email"
            required={!!requiredFields}
            inputId="ec-parent-email"
          >
            <input
              id="ec-parent-email"
              type="email"
              value={form.parent_email}
              onChange={(e) => handleFormChange("parent_email", e.target.value)}
              required={!!requiredFields}
              autoComplete="off"
            />
          </EcFieldBlock>
          <EcFieldBlock
            label="Phone"
            required={!!requiredFields}
            inputId="ec-parent-phone"
          >
            <PhoneInput
              id="ec-parent-phone"
              value={form.parent_phone}
              onChange={(v) => handleFormChange("parent_phone", v)}
              required={!!requiredFields}
              autoComplete="off"
            />
          </EcFieldBlock>
        </div>
      </>
    );
  }

  const loadEvents = useCallback(() => {
    if (!account_id) return;
    setLoading(true);
    setBannerError("");
    apiFetch(serverUrl + "/getOpenEvents", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setLoading(false));
  }, [account_id, serverUrl]);

  const loadPrograms = useCallback(() => {
    if (!account_id) {
      setPrograms([]);
      return;
    }
    apiFetch(serverUrl + "/getProgram", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]));
  }, [account_id, serverUrl]);

  useEffect(() => {
    loadEvents();
    loadPrograms();
  }, [loadEvents, loadPrograms]);

  // When logged in, load registration rows (per participant) and linked children for parents (family availability).
  const loadMyRegisteredEventIds = useCallback(() => {
    if (!account_id || !user || !userInfo?.email_id) {
      setMyRegisteredEvents([]);
      setLinkedChildUserIds([]);
      setLinkedChildrenUsers([]);
      setRegistrationFamilyContextLoading(false);
      return;
    }
    const email = (userInfo.email_id || "").trim();
    if (!email) {
      setMyRegisteredEvents([]);
      setLinkedChildUserIds([]);
      setLinkedChildrenUsers([]);
      setRegistrationFamilyContextLoading(false);
      return;
    }
    setRegistrationFamilyContextLoading(true);
    const myEventsPromise = apiFetch(serverUrl + "/getMyEvents", {
      method: "POST",
      body: JSON.stringify({ account_id, participant_email: email }),
    }).then((resp) => resp.json());

    const childrenPromise =
      userInfo && userInfoHasParentRole(userInfo) && userInfo._id
        ? apiFetch(serverUrl + "/getLinkedChildrenForParent", {
            method: "POST",
            body: JSON.stringify({
              account_id,
              parent_user_id: String(userInfo._id),
              parent_email: email,
            }),
          })
            .then((resp) => (resp.ok ? resp.json() : []))
            .then((data) => (Array.isArray(data) ? data : []))
        : Promise.resolve([]);

    Promise.all([myEventsPromise, childrenPromise])
      .then(([data, childrenList]) => {
        const list = Array.isArray(data) ? data : [];
        const kids = Array.isArray(childrenList) ? childrenList : [];
        setMyRegisteredEvents(list);
        setLinkedChildrenUsers(kids);
        setLinkedChildUserIds(
          kids.map((u) => (u && u._id != null ? String(u._id) : "")).filter(Boolean)
        );
      })
      .catch(() => {
        setMyRegisteredEvents([]);
        setLinkedChildUserIds([]);
        setLinkedChildrenUsers([]);
      })
      .finally(() => setRegistrationFamilyContextLoading(false));
  }, [account_id, serverUrl, user, userInfo]);

  useEffect(() => {
    loadMyRegisteredEventIds();
  }, [loadMyRegisteredEventIds]);

  useEffect(() => {
    // Reset bulk selection state when switching account or tab
    setSelectedEventIds(new Set());
    setBulkMode(false);
    setBulkEvents([]);
  }, [account_id, activeTab]);

  useEffect(() => {
    setEventListSearchTerm("");
    setRegisteredStatusFilter("open");
  }, [account_id]);

  const upcomingEvents = useMemo(() => getUpcomingEventsSorted(events), [events]);

  /** event id -> Set of participant_user_id with an active (non-cancelled) registration */
  const registeredParticipantsByEventId = useMemo(() => {
    const m = new Map();
    for (const row of myRegisteredEvents || []) {
      const eid = String(row._id);
      const uid = row.participant_user_id != null ? String(row.participant_user_id) : "";
      if (!uid) continue;
      if (!m.has(eid)) m.set(eid, new Set());
      m.get(eid).add(uid);
    }
    return m;
  }, [myRegisteredEvents]);

  // Parent bulk: default and sync per-event checkboxes (bulk_event_attend) on each attendee row.
  useEffect(() => {
    if (!bulkMode || guestMode !== "parent" || !userInfo?._id) return;
    const evs = bulkEvents || [];
    if (evs.length === 0) return;
    const eventIds = evs.map((e) => String(e._id));
    setChildren((prev) => {
      if (!prev || prev.length === 0) return prev;
      let anyRowChanged = false;
      const next = prev.map((c) => {
        if (!c) return c;
        const m = { ...(c.bulk_event_attend || {}) };
        let rowChanged = false;
        for (const ev of evs) {
          const eid = String(ev._id);
          if (m[eid] === undefined) {
            // Parent self-row is only selectable for All Ages (not Youth Only).
            if (c.is_parent_self && !parentIsRegistrableAttendeeForEvent(ev)) {
              m[eid] = false;
            } else {
              m[eid] = !childAlreadyRegisteredForEvent(c, eid, registeredParticipantsByEventId);
            }
            rowChanged = true;
          }
        }
        for (const k of Object.keys(m)) {
          if (!eventIds.includes(k)) {
            delete m[k];
            rowChanged = true;
          }
        }
        const attendsAny = evs.some((ev) => {
          const eid = String(ev._id);
          if (c.is_parent_self && !parentIsRegistrableAttendeeForEvent(ev)) return false;
          return !!m[eid] && !childAlreadyRegisteredForEvent(c, eid, registeredParticipantsByEventId);
        });
        if (c.attend !== attendsAny) rowChanged = true;
        if (!rowChanged) return c;
        anyRowChanged = true;
        return { ...c, bulk_event_attend: m, attend: attendsAny };
      });
      return anyRowChanged ? next : prev;
    });
    // `children` must be a dependency: rows load async after bulk opens; without this run,
    // bulk_event_attend never initializes and every checkbox renders unchecked.
  }, [bulkMode, guestMode, userInfo, bulkEvents, registeredParticipantsByEventId, children]);

  // Available tab: show an event if at least one eligible registrant is not yet registered for it.
  const availableEvents = useMemo(() => {
    if (!user || !userInfo?._id) {
      return upcomingEvents;
    }
    return upcomingEvents.filter((ev) => {
      const eid = String(ev._id);
      const regs = registeredParticipantsByEventId.get(eid);
      if (!regs || regs.size === 0) return true;
      const checkIds = getUserIdsForEventAvailabilityCheck(ev, userInfo, linkedChildUserIds);
      for (const uid of checkIds) {
        if (!regs.has(uid)) return true;
      }
      return false;
    });
  }, [upcomingEvents, registeredParticipantsByEventId, user, userInfo, linkedChildUserIds]);

  // QR event picker: open (not Closed) registrations with upcoming start days.
  const upcomingRegisteredEvents = useMemo(
    () =>
      getUpcomingEventsSorted(
        (myRegisteredEvents || []).filter((ev) => (ev?.status || "").trim() !== "Closed")
      ),
    [myRegisteredEvents]
  );

  // My Registered Events tab: full getMyEvents set, narrowed by Open/Expired/Closed (Events semantics).
  const registeredStatusFilterSet = useMemo(() => {
    if (registeredStatusFilter === "all") return new Set();
    if (registeredStatusFilter === "expired") return new Set(["Expired"]);
    if (registeredStatusFilter === "closed") return new Set(["Closed"]);
    return new Set(["Open"]);
  }, [registeredStatusFilter]);

  const statusFilteredRegisteredEvents = useMemo(
    () => filterEventsByOpenClosedStatus(myRegisteredEvents, registeredStatusFilterSet),
    [myRegisteredEvents, registeredStatusFilterSet]
  );

  const eventListSearchLower = useMemo(
    () => (eventListSearchTerm || "").trim().toLowerCase(),
    [eventListSearchTerm]
  );
  const loggedInUserIdForEventSearch = userInfo?._id != null ? String(userInfo._id) : "";

  const filteredAvailableEvents = useMemo(() => {
    if (!eventListSearchLower) return availableEvents;
    return (availableEvents || []).filter((ev) =>
      eventCalendarAvailableMatchesSearch(ev, eventListSearchLower)
    );
  }, [availableEvents, eventListSearchLower]);

  const filteredRegisteredEvents = useMemo(() => {
    if (!eventListSearchLower) return statusFilteredRegisteredEvents;
    return (statusFilteredRegisteredEvents || []).filter((ev) =>
      eventCalendarRegistrationRowMatchesSearch(ev, loggedInUserIdForEventSearch, eventListSearchLower)
    );
  }, [statusFilteredRegisteredEvents, eventListSearchLower, loggedInUserIdForEventSearch]);

  const programFilterSourceEvents = useMemo(() => {
    if (activeTab === "available") return filteredAvailableEvents;
    if (activeTab === "registered") return filteredRegisteredEvents;
    return [];
  }, [activeTab, filteredAvailableEvents, filteredRegisteredEvents]);

  const programById = useMemo(() => {
    return Object.fromEntries((programs || []).map((p) => [String(p._id), p]));
  }, [programs]);

  const programCounts = useMemo(() => {
    const map = new Map();
    for (const ev of programFilterSourceEvents || []) {
      const id = getEventProgramFilterId(ev);
      const prev = map.get(id) || { id, label: getEventProgramLabel(ev, programById), count: 0 };
      map.set(id, { ...prev, count: (prev.count || 0) + 1 });
    }
    const list = Array.from(map.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return { list, total: (programFilterSourceEvents || []).length };
  }, [programFilterSourceEvents, programById]);

  const isEventHeroCardTab =
    activeTab === "available" || activeTab === "registered";

  const effectiveProgramFilterId = isEventHeroCardTab ? programFilterId : PROGRAM_FILTER_ALL;

  const programFilteredAvailableEvents = useMemo(() => {
    if (!effectiveProgramFilterId) return filteredAvailableEvents;
    return (filteredAvailableEvents || []).filter((ev) => getEventProgramFilterId(ev) === effectiveProgramFilterId);
  }, [filteredAvailableEvents, effectiveProgramFilterId]);

  const programFilteredRegisteredEvents = useMemo(() => {
    if (!effectiveProgramFilterId) return filteredRegisteredEvents;
    return (filteredRegisteredEvents || []).filter(
      (ev) => getEventProgramFilterId(ev) === effectiveProgramFilterId
    );
  }, [filteredRegisteredEvents, effectiveProgramFilterId]);

  const activeEventCalendarItems = useMemo(() => {
    if (activeTab === "available") return programFilteredAvailableEvents;
    if (activeTab === "registered") return user ? programFilteredRegisteredEvents : [];
    return [];
  }, [
    activeTab,
    programFilteredAvailableEvents,
    programFilteredRegisteredEvents,
    user,
  ]);

  const sortedAvailableEventsForTable = useMemo(() => {
    const decorated = (programFilteredAvailableEvents || []).map((ev) => ({
      __orig: ev,
      name: ev?.name || "",
      start_datetime: ev?.start_datetime || "",
      organizer_name: ev?.organizer_name || "",
      audience_type: (ev?.audience_type || "").trim(),
      location: formatPlaceholderAwareEventLocation(ev),
      status: getEventUiPhase(ev),
    }));
    return sortRowsByConfig(decorated, eventListSortConfig, { start_datetime: "date" }).map(
      (r) => r.__orig
    );
  }, [programFilteredAvailableEvents, eventListSortConfig]);

  const sortedRegisteredEventsForTable = useMemo(() => {
    const loggedInId = userInfo?._id;
    const decorated = (programFilteredRegisteredEvents || []).map((ev) => ({
      __orig: ev,
      name: ev?.name || "",
      start_datetime: ev?.start_datetime || "",
      participant: getMyEventsParticipantDisplay(ev, loggedInId),
      attendance: formatMyRegistrationStatusLabel(ev?.attendance),
      organizer_name: ev?.organizer_name || "",
      location: formatPlaceholderAwareEventLocation(ev),
      status: getEventUiPhase(ev),
    }));
    return sortRowsByConfig(decorated, eventListSortConfig, { start_datetime: "date" }).map(
      (r) => r.__orig
    );
  }, [programFilteredRegisteredEvents, eventListSortConfig, userInfo?._id]);

  const participantQrEntries = useMemo(() => {
    if (!user || !userInfo?._id) return [];
    return buildEventCalendarParticipantQrEntries(userInfo, linkedChildrenUsers);
  }, [user, userInfo, linkedChildrenUsers]);

  /** Tab badge: household check-in QR count (event filter is inside the tab). */
  const participantQrTabCount = user ? participantQrEntries.length : 0;

  const eventCalendarTabPanelId =
    activeTab === "available"
      ? "EventCalendar-panel-available"
      : activeTab === "participant-qr"
        ? "EventCalendar-panel-participant-qr"
        : activeTab === "volunteer-opportunities"
          ? "EventCalendar-panel-volunteer-opportunities"
          : "EventCalendar-panel-registered";
  const eventCalendarTabPanelLabelledBy =
    activeTab === "available"
      ? "EventCalendar-tab-available"
      : activeTab === "participant-qr"
        ? "EventCalendar-tab-participant-qr"
        : activeTab === "volunteer-opportunities"
          ? "EventCalendar-tab-volunteer-opportunities"
          : "EventCalendar-tab-registered";

  const loadLinkedChildrenForParent = useCallback(
    async (eventAccountId, registeredUserIdsForThisEvent = null, eventsForContext = null) => {
      if (!eventAccountId || !userInfo?._id || !(userInfo.email_id || "").trim()) {
        return withParentSelfAttendeeRow([], userInfo, eventsForContext, registeredUserIdsForThisEvent);
      }
      try {
        const resp = await apiFetch(serverUrl + "/getLinkedChildrenForParent", {
          method: "POST",
          body: JSON.stringify({
            account_id: eventAccountId,
            parent_user_id: String(userInfo._id),
            parent_email: (userInfo.email_id || "").trim(),
          }),
        });
        if (!resp.ok) {
          return withParentSelfAttendeeRow([], userInfo, eventsForContext, registeredUserIdsForThisEvent);
        }
        const data = await resp.json();
        const list = Array.isArray(data) ? data : [];
        let rows = list.map(userDocToChildRow);
        if (registeredUserIdsForThisEvent instanceof Set) {
          rows = rows.map((r) => {
            const uid = (r.linked_user_id || "").trim();
            const already = !!(uid && registeredUserIdsForThisEvent.has(uid));
            return {
              ...r,
              attend: !already,
              already_registered_for_event: already,
            };
          });
        }
        return withParentSelfAttendeeRow(rows, userInfo, eventsForContext, registeredUserIdsForThisEvent);
      } catch {
        return withParentSelfAttendeeRow([], userInfo, eventsForContext, registeredUserIdsForThisEvent);
      }
    },
    [serverUrl, userInfo]
  );

  // After login / parent modal open: load linked children (+ parent self for All Ages) once per context.
  const parentAttendeesLoadKeyRef = useRef("");
  useEffect(() => {
    if (guestMode !== "parent" || !userInfo?._id || !account_id) {
      parentAttendeesLoadKeyRef.current = "";
      return undefined;
    }
    const eventsForContext = bulkMode
      ? bulkEvents
      : modalEvent
        ? [modalEvent]
        : [];
    if (!eventsForContext || eventsForContext.length === 0) return undefined;
    if (!shouldOpenParentRegistrationFlow(userInfo, eventsForContext)) return undefined;
    const key = [
      bulkMode ? "bulk" : "single",
      String(userInfo._id),
      eventsForContext.map((e) => String(e?._id || "")).join(","),
    ].join("|");
    if (parentAttendeesLoadKeyRef.current === key) return undefined;
    if ((children || []).length > 0) {
      parentAttendeesLoadKeyRef.current = key;
      return undefined;
    }
    parentAttendeesLoadKeyRef.current = key;
    let cancelled = false;
    const regsForEv =
      !bulkMode && modalEvent
        ? registeredParticipantsByEventId.get(String(modalEvent._id)) || new Set()
        : null;
    const aid = (!bulkMode && modalEvent?.account_id) || account_id;
    loadLinkedChildrenForParent(aid, regsForEv, eventsForContext).then((rows) => {
      if (!cancelled) setChildren(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [
    guestMode,
    userInfo,
    account_id,
    children,
    bulkMode,
    bulkEvents,
    modalEvent,
    loadLinkedChildrenForParent,
    registeredParticipantsByEventId,
  ]);

  const attendEventId = searchParams.get("attendEventId");
  const deepLinkEventId = attendEventId;

  const openProfileSignup = useCallback((options = {}) => {
    // Ensure actions are clickable when opening profile signup.
    setSubmitting(false);
    if (!eventSignupModeAllowsAnySignup(eventSignupMode)) {
      openAlert("Account signup is not available for this organization.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }
    setGatewayMode(resolveEventSignupGatewayMode(options.mode || "choice", eventSignupMode));
    setSignupPassword("");
    setSignupConfirmPassword("");
    setCoppaPrivacyConsent(false);
    setSmsOptIn(false);
    setEmailCheckStatus({ checking: false, inUse: false, message: "" });
    setProfileSignupOpen(true);
  }, [eventSignupMode]);

  const closeProfileSignup = useCallback(() => {
    setProfileSignupOpen(false);
    setSubmitting(false);
    setGatewayMode("choice");
    setSignupPassword("");
    setSignupConfirmPassword("");
    setCoppaPrivacyConsent(false);
    setSmsOptIn(false);
    setEmailCheckStatus({ checking: false, inUse: false, message: "" });
  }, []);

  useEffect(() => {
    if (gatewayMode !== "signup-parent") {
      setCoppaPrivacyConsent(false);
    }
    if (gatewayMode !== "signup-parent" && gatewayMode !== "signup-self") {
      setSmsOptIn(false);
    }
  }, [gatewayMode]);

  // When landing via QR / vanity `/e/{slug}`: open registration modal and go straight to name/phone form
  useEffect(() => {
    if (!deepLinkEventId || upcomingEvents.length === 0) return;
    const ev = upcomingEvents.find((e) => String(e._id) === deepLinkEventId);
    if (!ev) return;
    if (isPlaceholderEvent(ev)) {
      openAlert(EVENT_PLACEHOLDER_REGISTRATION_MESSAGE, "Registration not open");
      return;
    }
    setModalEvent(ev);
    // Gatekeeper: if not signed in, show account-access required in the registration modal.
    if (!user || !userInfo) {
      pendingAfterAuthRef.current = { type: "self-single", event: ev };
      return;
    }
    if (shouldOpenParentRegistrationFlow(userInfo, ev)) {
      setGuestMode("parent");
      setForm(getParentFormPrefillFromUser(userInfo) || { ...EMPTY_ATTEND_REGISTRATION_FORM });
      const aid = ev.account_id || account_id;
      const regsForEv = registeredParticipantsByEventId.get(String(ev._id)) || new Set();
      let cancelled = false;
      loadLinkedChildrenForParent(aid, regsForEv, [ev]).then((rows) => {
        if (!cancelled) {
          setChildren(rows);
        }
      });
      setAttendStep("guest-form");
      return () => {
        cancelled = true;
      };
    }
    setChildren([emptyChild()]);
    setGuestMode("self");
    setForm(getFormPrefillFromUser(userInfo) || EMPTY_ATTEND_REGISTRATION_FORM);
    setAttendStep("guest-form");
    return undefined;
  }, [deepLinkEventId, user, userInfo, upcomingEvents, account_id, loadLinkedChildrenForParent, registeredParticipantsByEventId, openProfileSignup]);

  // When landing via QR / vanity URL, auto-select the event card so it shows as selected
  useEffect(() => {
    if (!deepLinkEventId || availableEvents.length === 0) return;
    const match = availableEvents.find((e) => String(e._id) === deepLinkEventId);
    if (!match || isPlaceholderEvent(match)) return;
    setSelectedEventIds((prev) => (prev.has(deepLinkEventId) ? prev : new Set([...prev, deepLinkEventId])));
  }, [deepLinkEventId, availableEvents]);

  function goToEventCalendarLogin() {
    const params = new URLSearchParams();
    params.set(
      "returnUrl",
      buildEventCalendarLoginReturnPath({
        accountId: account_id,
        attendEventId: attendEventId || undefined,
        includeAccountQuery: !subdomain,
      })
    );
    window.location.assign(`/Login?${params.toString()}`);
  }

  function closeModal() {
    setModalEvent(null);
    setBulkMode(false);
    setBulkEvents([]);
    setAttendStep("guest-form");
    setGuestMode("self");
    setGatewayMode("choice");
    setSignupPassword("");
    setSignupConfirmPassword("");
    setEmailCheckStatus({ checking: false, inUse: false, message: "" });
    pendingAfterAuthRef.current = null;
    setChildren([emptyChild()]);
    if (attendEventId) {
      const next = new URLSearchParams(searchParams);
      next.delete("attendEventId");
      setSearchParams(next, { replace: true });
    }
  }

  function toggleEventSelection(eventId, checked) {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      const id = String(eventId);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  /**
   * Opens the multi-event registration modal for a set of event ids (defaults to current selection).
   * Pass `additionalEventIds` to include specific events (e.g. "Register now" on a card) merged with the current selection.
   * @param {{ additionalEventIds?: Array<string|number> }} [opts]
   */
  function startBulkRegistration(opts = {}) {
    const additional = Array.isArray(opts.additionalEventIds) ? opts.additionalEventIds : [];
    // Gatekeeper: require auth before starting bulk registration.
    if (!user || !userInfo) {
      const firstId = additional[0] != null ? String(additional[0]).trim() : "";
      const ev = firstId ? availableEvents.find((e) => String(e._id) === firstId) : null;
      if (ev && !isPlaceholderEvent(ev)) {
        setModalEvent(ev);
        pendingAfterAuthRef.current = { type: "self-single", event: ev };
        return;
      }
      openAlert(
        "Account access is required to register. Log in or sign up to continue.",
        "Account access required"
      );
      return;
    }
    const idSet = new Set(selectedEventIds);
    for (const raw of additional) {
      const sid = raw != null ? String(raw).trim() : "";
      if (sid) idSet.add(sid);
    }
    if (additional.length > 0) {
      setSelectedEventIds(new Set(idSet));
    }
    if (idSet.size === 0) {
      openAlert("Select at least one event to register.", ALERT_TITLE_SELECT_EVENTS_FIRST);
      return;
    }
    const selectedList = availableEvents.filter((ev) => idSet.has(String(ev._id)));
    if (selectedList.length === 0) {
      openAlert(
        "The selected events are no longer available. Please refresh and try again.",
        ALERT_TITLE_EVENTS_UNAVAILABLE
      );
      setSelectedEventIds(new Set());
      return;
    }
    const placeholderSelected = selectedList.filter((ev) => isPlaceholderEvent(ev));
    if (placeholderSelected.length > 0) {
      openAlert(
        EVENT_PLACEHOLDER_REGISTRATION_MESSAGE +
          (placeholderSelected.length > 1
            ? `\n\nPlaceholder events:\n${placeholderSelected.map((e) => `- ${e.name || "Event"}`).join("\n")}`
            : ""),
        "Registration not open"
      );
      return;
    }
    const paidEvents = selectedList.filter((ev) => blocksPaidBulkRegistration(ev));
    if (paidEvents.length > 0) {
      openAlert(
        "Bulk registration is available for free events and Payment Link paid events.\n\n" +
          "The following events require individual registration (integrated Stripe Checkout or unpaid configuration):\n" +
          paidEvents.map((e) => `- ${e.name || "Event"}`).join("\n"),
        ALERT_TITLE_REGISTER_PAID_INDIVIDUALLY
      );
      return;
    }
    const useParentBulk = shouldOpenParentRegistrationFlow(userInfo, selectedList);
    setGuestMode(useParentBulk ? "parent" : "self");
    setBulkEvents(selectedList);
    setBulkMode(true);
    setModalEvent(null);
    setForm(
      useParentBulk
        ? getParentFormPrefillFromUser(userInfo) || { ...EMPTY_ATTEND_REGISTRATION_FORM }
        : user && userInfo
          ? getFormPrefillFromUser(userInfo) || EMPTY_ATTEND_REGISTRATION_FORM
          : EMPTY_ATTEND_REGISTRATION_FORM
    );
    if (useParentBulk && account_id) {
      setChildren([]);
      loadLinkedChildrenForParent(account_id, null, selectedList).then((rows) => {
        setChildren(rows);
      });
    } else {
      setChildren([emptyChild()]);
    }
    setAttendStep("guest-form");
  }

  function handleFormChange(field, value) {
    const nextValue =
      field === "phone" || field === "parent_phone"
        ? formatUsPhoneAsYouType(value)
        : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  }

  function validateOptionalEmailField(value, label) {
    const s = (value || "").trim();
    if (!s) return "";
    const check = validateEmail(s);
    return check.valid ? "" : (check.message || `Invalid ${label || "email"}.`);
  }

  function validateOptionalPhoneField(value, label) {
    const s = (value || "").trim();
    if (!s) return "";
    const check = validatePhone(s);
    return check.valid ? "" : (check.message || `Invalid ${label || "phone"}.`);
  }

  function normalizeEmailForUniq(value) {
    const s = (value == null ? "" : String(value)).trim().toLowerCase();
    return s;
  }

  function normalizePhoneForUniq(value) {
    const s = (value == null ? "" : String(value)).trim();
    if (!s) return "";
    // Compare by digits so formatting differences don't bypass uniqueness.
    const digits = s.replace(/[^\d]/g, "");
    return digits;
  }

  function validateParentRegistrationInputs({ eventsForContext, bulkParentMatrix, skipParentContact }) {
    const errors = [];
    const eventsList = Array.isArray(eventsForContext) ? eventsForContext : [];
    const anyYouthOnly = eventsList.some(isYouthOnlyEvent);
    const eventIds = eventsList.map((e) => String(e?._id || "")).filter(Boolean);
    const useBulkMatrix = !!bulkParentMatrix && eventIds.length > 0;
    const includesParentSelf = (Array.isArray(children) ? children : []).some((c) => c?.is_parent_self);
    const personWord = includesParentSelf ? "participant" : "child";

    const pFirst = (form.parent_first_name || "").trim();
    const pLast = (form.parent_last_name || "").trim();
    const pEmail = (form.parent_email || "").trim();
    const pPhone = (form.parent_phone || "").trim();
    if (!skipParentContact) {
      if (!pFirst || !pLast) errors.push("Parent/guardian first and last name are required.");
      if (!pEmail) errors.push("Parent/guardian email is required.");
      else {
        const pEmailCheck = validateEmail(pEmail);
        if (!pEmailCheck.valid) errors.push(pEmailCheck.message || "Invalid parent/guardian email.");
      }
      if (!pPhone) errors.push("Parent/guardian phone is required.");
      else {
        const pPhoneCheck = validatePhone(pPhone);
        if (!pPhoneCheck.valid) errors.push(pPhoneCheck.message || "Invalid parent/guardian phone.");
      }
    }
    // Extended parent profile fields are required for Youth Only program registration.
    if (anyYouthOnly) {
      const zipErr = validateRequiredUsZip(form.parent_zip_code);
      if (zipErr) errors.push(zipErr);
      const eduErr = validateRequiredEducationLevel(form.parent_highest_education_level);
      if (eduErr) errors.push(eduErr);
      const hearErr = validateRequiredHearAboutUs(form.parent_hear_about_us, form.parent_hear_about_us_other);
      if (hearErr) errors.push(hearErr);
    }

    const list = Array.isArray(children) ? children : [];
    if (list.length === 0) {
      errors.push(includesParentSelf ? "Please add at least one participant." : "Please add at least one child.");
      return errors;
    }
    if (useBulkMatrix) {
      for (const ev of eventsList) {
        const eid = String(ev._id);
        const countForEvent = list.filter((c) => {
          if (!c) return false;
          const m = c.bulk_event_attend || {};
          return !!m[eid] && bulkParentCellIsToggleable(c, ev, registeredParticipantsByEventId);
        }).length;
        if (countForEvent < 1) {
          errors.push(
            `Select at least one ${personWord} for each event. None selected for "${(ev.name || "Event").trim()}".`
          );
        }
      }
    } else {
      const selected = list.filter((c) => c && c.attend);
      if (selected.length === 0) {
        errors.push(`Select at least one ${personWord} to register for this event.`);
      }
    }

    // Uniqueness checks across parent + all children (entered values only).
    const emailSeen = new Map(); // normalized -> label
    const phoneSeen = new Map(); // normalizedDigits -> label
    const parentEmailNorm = normalizeEmailForUniq(pEmail);
    if (parentEmailNorm) emailSeen.set(parentEmailNorm, "Parent/guardian email");
    const parentPhoneNorm = normalizePhoneForUniq(pPhone);
    if (parentPhoneNorm) phoneSeen.set(parentPhoneNorm, "Parent/guardian phone");

    for (let i = 0; i < list.length; i++) {
      const c = list[i];
      if (!c) continue;
      const rowLabel = c.is_parent_self ? "Parent" : `Child ${i + 1}`;
      const isAttending = useBulkMatrix
        ? eventsList.some(
            (ev) =>
              !!(c.bulk_event_attend || {})[String(ev._id)] &&
              bulkParentCellIsToggleable(c, ev, registeredParticipantsByEventId)
          )
        : !!c.attend;
      if (!useBulkMatrix && isAttending && c.already_registered_for_event) {
        errors.push(
          `${rowLabel} is already registered for this event. Uncheck them or remove the row before submitting.`
        );
      }
      const first = (c.first_name || "").trim();
      const last = (c.last_name || "").trim();
      if (isAttending && (!first || !last)) {
        errors.push(`${rowLabel}: first name and last name are required.`);
      }
      // Parent self-row uses the same contact as parent/guardian — skip duplicate uniqueness + youth fields.
      if (c.is_parent_self) continue;

      const emailErr = validateOptionalEmailField(c.email, `child ${i + 1} email`);
      if (emailErr) errors.push(emailErr);
      const phoneErr = validateOptionalPhoneField(c.phone, `child ${i + 1} phone`);
      if (phoneErr) errors.push(phoneErr);

      const childEmailNorm = normalizeEmailForUniq(c.email);
      if (childEmailNorm) {
        const existing = emailSeen.get(childEmailNorm);
        if (existing) {
          errors.push(`Email addresses must be unique. Child ${i + 1} email matches "${existing}".`);
        } else {
          emailSeen.set(childEmailNorm, `Child ${i + 1} email`);
        }
      }
      const childPhoneNorm = normalizePhoneForUniq(c.phone);
      if (childPhoneNorm) {
        const existing = phoneSeen.get(childPhoneNorm);
        if (existing) {
          errors.push(`Phone numbers must be unique. Child ${i + 1} phone matches "${existing}".`);
        } else {
          phoneSeen.set(childPhoneNorm, `Child ${i + 1} phone`);
        }
      }

      if (isAttending) {
        const genderErr = validateRequiredGender(c.gender);
        if (genderErr) errors.push(`Child ${i + 1}: ${genderErr}`);
        const raceErr = validateRequiredRaceEthnicity(c.race_ethnicity, c.race_ethnicity_other);
        if (raceErr) errors.push(`Child ${i + 1}: ${raceErr}`);
        const districtErr = validateRequiredSchoolDistrict(c.school_district);
        if (districtErr) errors.push(`Child ${i + 1}: ${districtErr}`);
      }

      if (anyYouthOnly && isAttending) {
        const dobErr = validateRequiredBirthMonthYear(c.birthday);
        if (dobErr) errors.push(`Child ${i + 1}: ${dobErr}`);
        const gradeRaw = (c.grade || "").trim();
        const gradeVal = mapGradeValue(gradeRaw);
        if (!gradeVal) errors.push(`Child ${i + 1}: please select a valid grade option.`);
        if (!(c.school || "").trim()) errors.push(`Child ${i + 1}: school is required for Youth Only events.`);
      }
    }
    return errors;
  }

  async function submitParentRegistrationSingle(e) {
    e.preventDefault();
    if (!modalEvent || !modalEvent._id) return;
    const eventAccountId = modalEvent.account_id || account_id;
    if (!eventAccountId) {
      openAlert("Unable to register: event account is missing.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }
    if (openValidationErrors(validateParentRegistrationInputs({ eventsForContext: [modalEvent] }))) {
      return;
    }

    const normalizedChildren = (children || []).map((c) => {
      const birthdayNorm = normalizeBirthMonthYearForSave((c?.birthday || "").trim());
      if (birthdayNorm.storageYmd && birthdayNorm.storageYmd !== (c?.birthday || "").trim()) {
        return { ...c, birthday: getBirthMonthYearInputValue(birthdayNorm.storageYmd) };
      }
      return c;
    });
    const body = {
      account_id: eventAccountId,
      event_id: modalEvent._id,
      parent_user_id: userInfo?._id ? String(userInfo._id) : null,
      parent: {
        first_name: (form.parent_first_name || "").trim(),
        last_name: (form.parent_last_name || "").trim(),
        email: (form.parent_email || "").trim(),
        phone: (form.parent_phone || "").trim(),
      },
      children: normalizedChildren.map((c) => ({
        attend: !!c.attend,
        user_id: (c.linked_user_id || "").trim() || null,
        first_name: (c.first_name || "").trim(),
        last_name: (c.last_name || "").trim(),
        email: (c.email || "").trim(),
        phone: (c.phone || "").trim(),
        birthday: normalizeBirthMonthYearForSave((c.birthday || "").trim()).storageYmd,
        gender: (c.gender || "").trim(),
        grade: mapGradeValue(c.grade || ""),
        school: (c.school || "").trim(),
        ...buildChildProfilePayload(c),
      })),
    };

    setSubmitting(true);
    apiFetch(serverUrl + "/registerEventParticipantsByParentUserIds", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((resp) => {
        if (resp.ok) return resp.json().catch(() => ({}));
        return resp.text().then((t) => Promise.reject(new Error(t)));
      })
      .then(async (data) => {
        const created = Number(data?.created || 0);
        const waitlisted = Number(data?.waitlisted || 0);
        const errors = Array.isArray(data?.errors) ? data.errors : [];
        const results = Array.isArray(data?.results) ? data.results : [];
        if (created === 0 && waitlisted === 0 && errors.length === 0 && results.length === 0) {
          openAlert("No registrations were created.", ALERT_TITLE_NO_REGISTRATIONS_CREATED);
        } else {
          const mapped = mapParentApiResultsToOutcomeRows(results, modalEvent?.name || "Event");
          const merged = mergeParentOutcomeFallbackRows(
            mapped.confirmed,
            mapped.waitlisted,
            created,
            waitlisted,
            modalEvent?.name || "Event"
          );
          openRegistrationOutcomeAlert({
            confirmed: merged.confirmed,
            waitlisted: merged.waitlisted,
            errors: [...errors, ...mapped.errors],
            paymentLinkUrls:
              data && data.requires_payment_link && data.payment_link_url
                ? [String(data.payment_link_url).trim()]
                : [],
          });
        }
        closeModal();
        loadEvents();
        loadMyRegisteredEventIds();
      })
      .catch((err) => openAlert(err?.message || "Registration failed.", ALERT_TITLE_REGISTRATION_FAILED))
      .finally(() => setSubmitting(false));
  }

  async function submitParentRegistrationBulk(e) {
    e.preventDefault();
    if (!account_id) {
      openAlert("Unable to register: account is missing.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }
    if (!bulkEvents || bulkEvents.length === 0) {
      openAlert("No events selected for bulk registration.", ALERT_TITLE_NO_EVENTS_SELECTED);
      return;
    }
    if (
      openValidationErrors(
        validateParentRegistrationInputs({
          eventsForContext: bulkEvents,
          bulkParentMatrix: true,
        })
      )
    ) {
      return;
    }
    setSubmitting(true);
    let created = 0;
    let waitlisted = 0;
    const errors = [];
    const results = [];
    const paymentLinkUrls = [];
    try {
      for (const ev of bulkEvents) {
        const eventAccountId = ev.account_id || account_id;
        if (!eventAccountId) {
          errors.push(`Unable to register for "${ev.name || "Event"}": event account is missing.`);
          continue;
        }
        try {
          const body = {
            account_id: eventAccountId,
            event_id: ev._id,
            parent_user_id: userInfo?._id ? String(userInfo._id) : null,
            parent: {
              first_name: (form.parent_first_name || "").trim(),
              last_name: (form.parent_last_name || "").trim(),
              email: (form.parent_email || "").trim(),
              phone: (form.parent_phone || "").trim(),
            },
            children: (children || []).map((c) => ({
              attend:
                !!(c.bulk_event_attend && c.bulk_event_attend[String(ev._id)]) &&
                bulkParentCellIsToggleable(c, ev, registeredParticipantsByEventId),
              user_id: (c.linked_user_id || "").trim() || null,
              first_name: (c.first_name || "").trim(),
              last_name: (c.last_name || "").trim(),
              email: (c.email || "").trim(),
              phone: (c.phone || "").trim(),
              birthday: normalizeBirthMonthYearForSave((c.birthday || "").trim()).storageYmd,
              gender: (c.gender || "").trim(),
              grade: mapGradeValue(c.grade || ""),
              school: (c.school || "").trim(),
              ...buildChildProfilePayload(c),
            })),
          };
          const resp = await apiFetch(serverUrl + "/registerEventParticipantsByParentUserIds", {
            method: "POST",
            body: JSON.stringify(body),
          });
          if (!resp.ok) {
            const text = await resp.text().catch(() => "");
            errors.push(text || `Registration failed for "${ev.name || "Event"}".`);
            continue;
          }
          const data = await resp.json().catch(() => ({}));
          created += Number(data?.created || 0);
          waitlisted += Number(data?.waitlisted || 0);
          if (data && data.requires_payment_link && data.payment_link_url) {
            paymentLinkUrls.push(String(data.payment_link_url).trim());
          }
          const errs = Array.isArray(data?.errors) ? data.errors : [];
          errs.forEach((m) => errors.push(`"${ev.name || "Event"}": ${m}`));
          const resList = Array.isArray(data?.results) ? data.results : [];
          resList.forEach((r) => {
            results.push({
              ...r,
              _eventName: ev.name || "Event",
            });
          });
        } catch (err) {
          errors.push((err && err.message) || `Registration failed for "${ev.name || "Event"}".`);
        }
      }
    } finally {
      setSubmitting(false);
    }
    if (!created && !waitlisted && errors.length === 0 && results.length === 0) {
      openAlert("No registrations were created.", ALERT_TITLE_NO_REGISTRATIONS_CREATED);
    } else {
      const mapped = mapParentApiResultsToOutcomeRows(results, "Selected events");
      const bulkDefaultName =
        bulkEvents && bulkEvents.length === 1 ? bulkEvents[0]?.name || "Event" : "Selected events";
      const merged = mergeParentOutcomeFallbackRows(
        mapped.confirmed,
        mapped.waitlisted,
        created,
        waitlisted,
        bulkDefaultName
      );
      openRegistrationOutcomeAlert({
        confirmed: merged.confirmed,
        waitlisted: merged.waitlisted,
        errors: [...errors, ...mapped.errors],
        paymentLinkUrls,
      });
    }
    setBulkMode(false);
    setBulkEvents([]);
    setSelectedEventIds(new Set());
    loadEvents();
    loadMyRegisteredEventIds();
  }

  function submitRegistration(e) {
    e.preventDefault();
    const first = (form.first_name || "").trim();
    const last = (form.last_name || "").trim();
    const errors = [];
    if (!first || !last) errors.push("First name and last name are required.");
    const emailCheck = validateEmail(form.email);
    if (!emailCheck.valid) errors.push(emailCheck.message || "Invalid email.");
    const phoneCheck = validatePhone(form.phone);
    if (!phoneCheck.valid) errors.push(phoneCheck.message || "Invalid phone.");
    if (openValidationErrors(errors)) return;
    if (!modalEvent || !modalEvent._id) return;
    const eventAccountId = modalEvent.account_id || account_id;
    if (!eventAccountId) {
      openAlert("Unable to register: event account is missing.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }
    const emailVal = (form.email || "").trim();
    const body = {
      account_id: eventAccountId,
      event_id: modalEvent._id,
      user_id: String(userInfo._id),
    };

    setSubmitting(true);
    apiFetch(serverUrl + "/registerEventParticipantByUserId", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((resp) => {
        if (resp.ok) return resp.json().catch(() => ({}));
        return resp.text().then((t) => Promise.reject(new Error(t)));
      })
      .then(async (data) => {
        if (data && data.requires_payment && data.participant_id && data.price_cents > 0) {
          const baseUrl = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";
          const successUrl = `${baseUrl}?account_id=${eventAccountId}&payment_success=1&event_name=${encodeURIComponent(data.event_name || modalEvent.name || "")}`;
          const cancelUrl = `${baseUrl}${account_id ? `?account_id=${account_id}` : ""}`;
          const checkoutResp = await apiFetch(serverUrl + "/createPaymentCheckoutSession", {
            method: "POST",
            body: JSON.stringify({
              account_id: eventAccountId,
              amount: Number(data.price_cents) / 100,
              workflow_type: "event_registration",
              workflow_ref: data.participant_id,
              customer_email: emailVal || undefined,
              success_url: successUrl,
              cancel_url: cancelUrl,
              description: data.event_name || modalEvent.name || "Event registration",
            }),
          });
          const checkoutData = await checkoutResp.json().catch(() => ({}));
          if (checkoutData.checkout_url) {
            closeModal();
            window.location.href = checkoutData.checkout_url;
            return;
          }
          openAlert(
            checkoutData.error || "Payment setup failed. Your registration is pending; contact the organizer.",
            ALERT_TITLE_PAYMENT_COULD_NOT_START
          );
          setSubmitting(false);
          return;
        }
        const isWaitlisted = data && data.attendance === "Waitlisted";
        const selfLabel = `Self: ${formatSelfDisplayName(form)}`;
        const eventName = modalEvent?.name || "Event";
        const row = {
          participantLabel: selfLabel,
          eventName,
          status: isWaitlisted ? "waitlisted" : "registered",
          detail: "",
        };
        const paymentLinkUrls =
          data && data.requires_payment_link && data.payment_link_url
            ? [String(data.payment_link_url).trim()]
            : [];
        // Email delivery failures are logged server-side only — do not show to end users.
        if (data && data.confirmation_email_sent === false) {
          console.warn(
            "[Event registration] Confirmation email was not sent:",
            data.confirmation_email_error || "email delivery is not configured"
          );
        }
        openRegistrationOutcomeAlert({
          confirmed: isWaitlisted ? [] : [row],
          waitlisted: isWaitlisted ? [row] : [],
          errors: [],
          paymentLinkUrls,
        });
        closeModal();
        loadEvents();
        loadMyRegisteredEventIds();
      })
      .catch((err) =>
        openAlert(err?.message || "Registration failed.", ALERT_TITLE_REGISTRATION_FAILED)
      )
      .finally(() => setSubmitting(false));
  }

  async function submitBulkRegistration(e) {
    e.preventDefault();
    const first = (form.first_name || "").trim();
    const last = (form.last_name || "").trim();
    const validationErrors = [];
    if (!first || !last) validationErrors.push("First name and last name are required.");
    const emailCheck = validateEmail(form.email);
    if (!emailCheck.valid) validationErrors.push(emailCheck.message || "Invalid email.");
    const phoneCheck = validatePhone(form.phone);
    if (!phoneCheck.valid) validationErrors.push(phoneCheck.message || "Invalid phone.");
    if (openValidationErrors(validationErrors)) return;
    if (!account_id) {
      openAlert("Unable to register: account is missing.", ALERT_TITLE_REGISTRATION_UNAVAILABLE);
      return;
    }
    if (!bulkEvents || bulkEvents.length === 0) {
      openAlert("No events selected for bulk registration.", ALERT_TITLE_NO_EVENTS_SELECTED);
      return;
    }
    const signedIn = !!(user && userInfo?._id);
    if (!signedIn) {
      // Account-first: bulk registration only available to signed-in users.
      pendingAfterAuthRef.current = { type: "self-bulk", events: bulkEvents };
      // UI should have gated before submit; keep a safe fallback.
      openAlert("Account access is required to register. Please log in or sign up.", "Account access required");
      return;
    }
    setSubmitting(true);
    const selfLabel = `Self: ${formatSelfDisplayName(form)}`;
    const confirmedRows = [];
    const waitlistedRows = [];
    const errors = [];
    const paymentLinkUrls = [];
    try {
      for (const ev of bulkEvents) {
        const eventAccountId = ev.account_id || account_id;
        if (!eventAccountId) {
          errors.push(
            `Unable to register for "${ev.name || "Event"}": event account is missing.`
          );
          continue;
        }
        try {
          const bulkBody = { account_id: eventAccountId, event_id: ev._id, user_id: String(userInfo._id) };
          const resp = await apiFetch(serverUrl + "/registerEventParticipantByUserId", {
            method: "POST",
            body: JSON.stringify(bulkBody),
          });
          if (!resp.ok) {
            const text = await resp.text();
            errors.push(
              text ||
                `Registration failed for "${ev.name || "Event"}". Please try again.`
            );
            continue;
          }
          const data = await resp.json().catch(() => ({}));
          if (data && data.requires_payment && data.price_cents > 0) {
            errors.push(
              `"${ev.name || "Event"}" requires payment. Please register for this event individually.`
            );
            continue;
          }
          if (data && data.requires_payment_link && data.payment_link_url) {
            paymentLinkUrls.push(String(data.payment_link_url).trim());
          }
          // Email delivery failures are logged server-side only — do not show to end users.
          if (data && data.confirmation_email_sent === false) {
            console.warn(
              `[Event registration] Confirmation email was not sent for "${ev.name || "Event"}":`,
              data.confirmation_email_error || "email delivery is not configured"
            );
          }
          const isWaitlisted = data && data.attendance === "Waitlisted";
          const eventName = ev.name || "Event";
          if (isWaitlisted) {
            waitlistedRows.push({
              participantLabel: selfLabel,
              eventName,
              status: "waitlisted",
              detail: "",
            });
          } else {
            confirmedRows.push({
              participantLabel: selfLabel,
              eventName,
              status: "registered",
              detail: "",
            });
          }
        } catch (err) {
          errors.push(
            (err && err.message) ||
              `Registration failed for "${ev.name || "Event"}".`
          );
        }
      }
    } finally {
      setSubmitting(false);
    }
    if (!confirmedRows.length && !waitlistedRows.length && errors.length === 0) {
      openAlert("No registrations were created.", ALERT_TITLE_NO_REGISTRATIONS_CREATED);
    } else {
      openRegistrationOutcomeAlert({
        confirmed: confirmedRows,
        waitlisted: waitlistedRows,
        errors,
        paymentLinkUrls,
      });
    }
    setBulkMode(false);
    setBulkEvents([]);
    setSelectedEventIds(new Set());
    loadEvents();
    loadMyRegisteredEventIds();
  }

  function cancelRegistration(ev) {
    const participantId = ev.participant_id;
    if (!participantId || !account_id) {
      openAlert("Unable to cancel: missing registration info.", ALERT_TITLE_CANNOT_CANCEL);
      return;
    }
    const childLabel = getRegisteredEventParticipantChildLabel(ev, userInfo?._id);
    const cancelTargetPhrase = childLabel
      ? `registration for ${childLabel}`
      : "your registration";
    setCancelConfirmDialog({
      open: true,
      title: "Cancel registration",
      message: `Cancel ${cancelTargetPhrase} for "${ev.name || "this event"}"? You can register again later if the event is still open.`,
      onConfirm: async () => {
        setCancellingParticipantId(participantId);
        try {
          const resp = await apiFetch(serverUrl + "/deleteEventParticipant", {
            method: "POST",
            body: JSON.stringify({ _id: participantId, account_id }),
          });
          if (!resp.ok) {
            const text = await resp.text().catch(() => "");
            throw new Error(text || "Cancel registration failed.");
          }
          loadEvents();
          loadMyRegisteredEventIds();
          setCancelConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        } finally {
          setCancellingParticipantId(null);
        }
      },
    });
  }

  return (
    <div style={{ minHeight: "100%", background: "#f5f5f5" }}>
      <div className="page-container page-container--section-tabs" style={{ background: "#f5f5f5", paddingBottom: 0, "--h-scroll-hint-bg": "#f5f5f5" }}>
        <SectionTabsPageHeader>
          <EventSectionTabs activeTab={EVENT_TAB_CALENDAR} />
        </SectionTabsPageHeader>
      </div>
      <header className="page-hero">
        <h1>Event Calendar</h1>
      </header>

      <div className="page-container page-container--section-tabs" style={{ flex: 1, minHeight: 0, paddingTop: 0, "--h-scroll-hint-bg": "#f5f5f5" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <p className="page-intro" style={{ flex: "1 1 520px", minWidth: 0, marginBottom: 0 }}>
          This is your go-to guide for all the upcoming activities, workshops, and gatherings
          organized by our foundation. We believe in celebrating and fostering achievement, and
          through these events, we hope to inspire, connect, and elevate our community's brightest
          minds. Whether you're a long-time supporter or a new friend, we invite you to explore,
          mark your calendars, and join us in our mission to recognize and uplift outstanding
          talent.
        </p>

        {account_id && !user && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "10px 12px",
              minWidth: 220,
              boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
            }}
            aria-label="Account access"
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
              Account Access
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={goToEventCalendarLogin}
                style={{
                  flex: "1 1 0",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #1f4fd6",
                  background: "#1f4fd6",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Log In
              </button>
              <button
                type="button"
                onClick={() => openProfileSignup({ mode: "choice" })}
                disabled={eventSignupSettingsLoading || !signupOptionsAvailable}
                style={{
                  flex: "1 1 0",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #1f4fd6",
                  background: "#fff",
                  color: "#1f4fd6",
                  fontWeight: 700,
                  cursor: eventSignupSettingsLoading || !signupOptionsAvailable ? "not-allowed" : "pointer",
                  opacity: eventSignupSettingsLoading || !signupOptionsAvailable ? 0.6 : 1,
                }}
              >
                Sign Up
              </button>
            </div>
          </div>
        )}
      </div>

      {bannerError && (
        <div
          role="alert"
          style={{ padding: "12px 16px", marginBottom: 8, background: "#f8d7da", color: "#842029", borderRadius: 4, fontSize: "0.875rem" }}
        >
          {bannerError}
        </div>
      )}
      <ConfirmationModal
        open={cancelConfirmDialog.open}
        title={cancelConfirmDialog.title || "Confirm"}
        message={cancelConfirmDialog.message}
        mode="confirm"
        onConfirm={() => {
          const fn = cancelConfirmDialog.onConfirm;
          if (!fn) return;
          return fn();
        }}
        onCancel={() => setCancelConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
        onError={(err) => setBannerError(getMessageForFetchError(err))}
      />
      <ConfirmationModal
        open={alertDialog.open}
        title={alertDialog.title || ALERT_TITLE_CHECK_DETAILS}
        message={alertDialog.message}
        mode="alert"
        confirmLabel={alertDialog.confirmLabel || undefined}
        contentMaxWidth={alertDialog.contentMaxWidth}
        onConfirm={handleAlertConfirm}
        onCancel={closeAlertDialog}
      />
      {paymentSuccessEventName && (
        <div className="EventCalendar-success" role="alert" style={{ padding: "12px 16px", marginBottom: "16px", background: "#d4edda", border: "1px solid #c3e6cb", borderRadius: "8px", color: "#155724" }}>
          <strong>Payment successful.</strong> You are registered for {decodeURIComponent(paymentSuccessEventName)}. {REGISTRATION_EMAIL_CODE_NOTE}
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("payment_success");
              next.delete("event_name");
              setSearchParams(next, { replace: true });
            }}
            style={{ marginLeft: "12px", padding: "2px 8px", cursor: "pointer" }}
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {loading && (
        <div className="empty-message" aria-live="polite">
          Loading events…
        </div>
      )}

      {account_id && !loading && (
        <>
          <div className="toolbar EventCalendar-event-list-toolbar">
            <div className="EventCalendar-event-list-search-wrap">
              <input
                type="text"
                value={eventListSearchTerm}
                onChange={(e) => setEventListSearchTerm(e.target.value)}
                placeholder={
                  activeTab === "participant-qr"
                    ? "Search participant name…"
                    : "Search event, description, location, program, organizer, participant, status, attendance…"
                }
                className="input input-min-200"
                aria-label={
                  activeTab === "participant-qr"
                    ? "Filter check-in QR codes by name"
                    : "Filter events in the current tab"
                }
              />
            </div>
            <button type="button" onClick={() => setEventListSearchTerm("")} className="btn btn-secondary">
              Clear
            </button>
          </div>
          <div className="tabs-bar tabs-bar-underline">
            <TabsList role="tablist" aria-label="Event calendar sections">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "available"}
                aria-controls="EventCalendar-panel-available"
                id="EventCalendar-tab-available"
                className={`tab-btn ${activeTab === "available" ? "active" : ""}`}
                onClick={() => setActiveTab("available")}
              >
                Available Events ({programFilteredAvailableEvents.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "registered"}
                aria-controls="EventCalendar-panel-registered"
                id="EventCalendar-tab-registered"
                className={`tab-btn ${activeTab === "registered" ? "active" : ""}`}
                onClick={() => setActiveTab("registered")}
              >
                My Registered Events ({programFilteredRegisteredEvents.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "participant-qr"}
                aria-controls="EventCalendar-panel-participant-qr"
                id="EventCalendar-tab-participant-qr"
                className={`tab-btn ${activeTab === "participant-qr" ? "active" : ""}`}
                onClick={() => setActiveTab("participant-qr")}
              >
                My Check-In QR Codes ({user ? participantQrTabCount : 0})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "volunteer-opportunities"}
                aria-controls="EventCalendar-panel-volunteer-opportunities"
                id="EventCalendar-tab-volunteer-opportunities"
                className={`tab-btn ${activeTab === "volunteer-opportunities" ? "active" : ""}`}
                onClick={handleVolunteerOpportunitiesTabClick}
              >
                Volunteer Opportunities
              </button>
            </TabsList>
          </div>
          {isEventHeroCardTab && (
            <div className="tabs-bar" style={{ marginTop: 8 }}>
              <TabsList role="group" aria-label="Filter events by program">
                <button
                  type="button"
                  className={`tab-btn ${programFilterId === PROGRAM_FILTER_ALL ? "active" : ""}`}
                  aria-pressed={programFilterId === PROGRAM_FILTER_ALL}
                  onClick={() => setProgramFilterId(PROGRAM_FILTER_ALL)}
                  title="Show all programs"
                >
                  <TabLabelWithCount label="All" count={programCounts.total} />
                </button>
                {programCounts.list.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`tab-btn ${programFilterId === p.id ? "active" : ""}`}
                    aria-pressed={programFilterId === p.id}
                    onClick={() => setProgramFilterId(p.id)}
                    title={p.label}
                  >
                    <TabLabelWithCount label={p.label} count={p.count} />
                  </button>
                ))}
              </TabsList>
            </div>
          )}
          {isEventHeroCardTab && (
            <div className="toolbar">
              <ViewModeIconToggle
                value={eventListViewMode}
                onChange={setEventListViewMode}
                modes={["calendar", "table", "card"]}
                ariaLabel="Event view"
              />
              {activeTab === "registered" && (
                <>
                  <span className="toolbar-divider" />
                  <OpenClosedFilterDropdown
                    value={registeredStatusFilter}
                    onChange={setRegisteredStatusFilter}
                    label="Status"
                    includeExpired
                  />
                </>
              )}
              {eventListViewMode === "calendar" && (
                <>
                  <span className="toolbar-divider" />
                  <GlobalTimeNavigatorBar
                    variant="inline"
                    title={eventListTimeNav.title}
                    view={eventListTimeNav.view}
                    onViewChange={eventListTimeNav.setView}
                    onToday={eventListTimeNav.goToday}
                    onPrev={eventListTimeNav.movePrev}
                    onNext={eventListTimeNav.moveNext}
                    viewOptions={["day", "week", "month"]}
                  />
                </>
              )}
            </div>
          )}
        </>
      )}

      <div
        className={
          isEventHeroCardTab && (eventListViewMode === "calendar" || eventListViewMode === "table")
            ? ""
            : `cards-wrapper${isEventHeroCardTab ? " cards-wrapper--events" : ""}`
        }
        style={{ paddingBottom: 40 }}
        id={eventCalendarTabPanelId}
        role="tabpanel"
        aria-labelledby={eventCalendarTabPanelLabelledBy}
      >
        {subdomainAccountLoading && !account_id && (
          <div className="empty-state">Loading calendar…</div>
        )}
        {subdomain && !subdomainAccountId && !subdomainAccountLoading && !account_id && (
          <div className="empty-state">
            No account found for this site. Check the URL or use your organization’s calendar link.
          </div>
        )}
        {!account_id && !loading && !subdomainAccountLoading && !(subdomain && !subdomainAccountId) && (
          <div className="empty-state">
            To view events, use your organization’s calendar link: <strong>/EventCalendar?account_id=YOUR_ACCOUNT_ID</strong>. You can also sign in to see your account’s events.
          </div>
        )}
        {account_id && !loading && registrationFamilyContextLoading && user && activeTab === "available" && (
          <div className="empty-state">Loading your registration information…</div>
        )}
        {account_id &&
          !loading &&
          !registrationFamilyContextLoading &&
          activeTab === "available" &&
          programFilteredAvailableEvents.length === 0 && (
          <div className="empty-state">
            {availableEvents.length === 0
              ? upcomingEvents.length === 0
                ? "No upcoming events at this time. Check back later."
                : "No events available to register. Everyone in your household who can register is already registered for all upcoming events."
              : "No events match your search. Try different keywords or clear the search."}
          </div>
        )}
        {account_id &&
          !loading &&
          activeTab === "participant-qr" &&
          !user && (
          <div
            className="EventCalendar-registered-guest-panel"
            role="region"
            aria-label="Sign in to view My Check-In QR Codes"
          >
            <div className="EventCalendar-registered-guest-art">
              <EventCalendarRegisteredGuestIllustration />
            </div>
            <div className="EventCalendar-registered-guest-copy">
              <p className="EventCalendar-registered-guest-line1">
                Your check-in QR codes appear here once you&apos;re signed in.
              </p>
              <p className="EventCalendar-registered-guest-line2">
                After you sign in, view household check-in codes and filter by event.
              </p>
              <button
                type="button"
                className="EventCalendar-registered-guest-cta"
                onClick={goToEventCalendarLogin}
              >
                Sign in
              </button>
            </div>
          </div>
        )}
        {account_id &&
          !loading &&
          activeTab === "participant-qr" &&
          user &&
          !registrationFamilyContextLoading &&
          participantQrEntries.length === 0 && (
          <div className="empty-state">
            No check-in QR codes are available for your account.
          </div>
        )}
        {account_id &&
          !loading &&
          activeTab === "registered" &&
          !user && (
          <div
            className="EventCalendar-registered-guest-panel"
            role="region"
            aria-label="Sign in to view your registered events"
          >
            <div className="EventCalendar-registered-guest-art">
              <EventCalendarRegisteredGuestIllustration />
            </div>
            <div className="EventCalendar-registered-guest-copy">
              <p className="EventCalendar-registered-guest-line1">
                Your registered events appear here once you&apos;re signed in.
              </p>
              <p className="EventCalendar-registered-guest-line2">
                Keep track of your foundation journey!
              </p>
              <button
                type="button"
                className="EventCalendar-registered-guest-cta"
                onClick={goToEventCalendarLogin}
              >
                Sign in to see events
              </button>
            </div>
          </div>
        )}
        {account_id &&
          !loading &&
          activeTab === "volunteer-opportunities" &&
          !user && (
          <div
            className="EventCalendar-registered-guest-panel"
            role="region"
            aria-label="Sign in to view volunteer opportunities"
          >
            <div className="EventCalendar-registered-guest-art">
              <EventCalendarRegisteredGuestIllustration />
            </div>
            <div className="EventCalendar-registered-guest-copy">
              <p className="EventCalendar-registered-guest-line1">
                Volunteer opportunities appear here once you&apos;re signed in.
              </p>
              <p className="EventCalendar-registered-guest-line2">
                Browse and sign up for ways to support our foundation.
              </p>
              <button
                type="button"
                className="EventCalendar-registered-guest-cta"
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set("returnUrl", "/VolunteerOpportunities");
                  window.location.assign(`/Login?${params.toString()}`);
                }}
              >
                Sign in to see opportunities
              </button>
            </div>
          </div>
        )}
        {account_id &&
          !loading &&
          activeTab === "registered" &&
          user &&
          programFilteredRegisteredEvents.length === 0 && (
          <div className="empty-state">
            {(myRegisteredEvents || []).length === 0
              ? "You are not registered for any events."
              : statusFilteredRegisteredEvents.length === 0
                ? registeredStatusFilter === "expired"
                  ? "You have no expired registered events."
                  : registeredStatusFilter === "closed"
                    ? "You have no closed registered events."
                    : "You have no open registered events."
                : "No events match your search. Try different keywords or clear the search."}
          </div>
        )}
        {account_id &&
          !loading &&
          !registrationFamilyContextLoading &&
          activeTab === "available" &&
          (eventListViewMode === "card" || eventListViewMode === "table") &&
          programFilteredAvailableEvents.length > 0 && (
          <div
            className="bulk-actions"
            id="EventCalendar-bulk-register-hint"
            aria-label="How registration and selection work"
          >
            <p className="bulk-actions-instruction">
              Use <strong>Add to selection</strong> on the events you want, then click{" "}
              <strong>Register now</strong> on any one of them to complete registration for{" "}
              <strong>all selected events</strong> in one step. You can also click{" "}
              <strong>Register now</strong> on a single event without selecting others first.
            </p>
          </div>
        )}
        {account_id &&
          !loading &&
          isEventHeroCardTab &&
          eventListViewMode === "calendar" &&
          (activeTab !== "available" || !registrationFamilyContextLoading) &&
          (activeTab === "available" || user) &&
          activeEventCalendarItems.length > 0 && (
          <EventScheduleCalendar
            items={activeEventCalendarItems}
            view={eventListTimeNav.view}
            focusDateYmd={eventListTimeNav.focusDateYmd}
            onItemClick={
              activeTab === "available"
                ? (event) => {
                    if (!isPlaceholderEvent(event)) {
                      startBulkRegistration({ additionalEventIds: [event._id] });
                    }
                  }
                : undefined
            }
          />
        )}
        {!loading &&
          !registrationFamilyContextLoading &&
          activeTab === "available" &&
          eventListViewMode === "table" &&
          programFilteredAvailableEvents.length > 0 && (
          <TableShell
            isEmpty={false}
            aria-label="Available events"
          >
            <table>
              <thead>
                <tr>
                  <SortableTableTh
                    sortKey="name"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Name
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="start_datetime"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Date
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="organizer_name"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Hosted By
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="audience_type"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Audience Type
                  </SortableTableTh>
                  <th scope="col">Price</th>
                  <SortableTableTh
                    sortKey="location"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Location
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="status"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Status
                  </SortableTableTh>
                  <TableActionColumnHeader align="left" />
                </tr>
              </thead>
              <tbody>
                {sortedAvailableEventsForTable.map((ev) => {
                  const isSelected = selectedEventIds.has(String(ev._id));
                  const placeholder = isPlaceholderEvent(ev);
                  const locationLabel = formatPlaceholderAwareEventLocation(ev);
                  return (
                    <tr key={ev._id}>
                      <td>{ev.name || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatPlaceholderAwareEventDate(ev, formatTimestampForTable)}
                      </td>
                      <td>{(ev.organizer_name || "").trim() || "—"}</td>
                      <td>{(ev.audience_type || "").trim() || "—"}</td>
                      <td><EventPriceBadge event={ev} /></td>
                      <td title={locationLabel}>{locationLabel}</td>
                      <td><EventCalendarOpenClosedStatusBadge event={ev} /></td>
                      <td>
                        {!placeholder ? (
                          <RowActionIcons>
                            <button
                              type="button"
                              className="btn-link"
                              style={iconBtnStyle}
                              title="Register now"
                              aria-label={`Register now for ${ev.name || "event"}${isSelected ? "" : " and include any other selected events"}`}
                              aria-describedby="EventCalendar-bulk-register-hint"
                              onClick={() => {
                                startBulkRegistration({ additionalEventIds: [ev._id] });
                              }}
                            >
                              <IconParticipants size={18} />
                            </button>
                            {isSelected ? (
                              <button
                                type="button"
                                className="btn-link-muted"
                                style={iconBtnStyle}
                                title="Remove from selection"
                                aria-label={`Remove ${ev.name || "event"} from selection`}
                                onClick={() => {
                                  if (!user || !userInfo) {
                                    openAlert(
                                      "Account access is required to register. Log in or sign up to continue.",
                                      "Account access required"
                                    );
                                    return;
                                  }
                                  toggleEventSelection(ev._id, false);
                                }}
                              >
                                <IconDelete size={18} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn-link"
                                style={iconBtnStyle}
                                title="Add to selection"
                                aria-label={`Add ${ev.name || "event"} to selection`}
                                onClick={() => {
                                  if (!user || !userInfo) {
                                    openAlert(
                                      "Account access is required to register. Log in or sign up to continue.",
                                      "Account access required"
                                    );
                                    return;
                                  }
                                  toggleEventSelection(ev._id, true);
                                }}
                              >
                                <IconSave size={18} />
                              </button>
                            )}
                          </RowActionIcons>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableShell>
        )}
        {!loading &&
          activeTab === "registered" &&
          user &&
          eventListViewMode === "table" &&
          programFilteredRegisteredEvents.length > 0 && (
          <TableShell
            isEmpty={false}
            aria-label="My registered events"
          >
            <table>
              <thead>
                <tr>
                  <SortableTableTh
                    sortKey="name"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Name
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="start_datetime"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Date
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="participant"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Participant
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="attendance"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Attendance
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="organizer_name"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Hosted By
                  </SortableTableTh>
                  <th scope="col">Price</th>
                  <SortableTableTh
                    sortKey="location"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Location
                  </SortableTableTh>
                  <SortableTableTh
                    sortKey="status"
                    currentSortKey={eventListSortConfig.key}
                    direction={eventListSortConfig.direction}
                    onSort={handleEventListSort}
                  >
                    Status
                  </SortableTableTh>
                  <TableActionColumnHeader align="left" />
                </tr>
              </thead>
              <tbody>
                {sortedRegisteredEventsForTable.map((ev) => {
                  const isEventClosed = (ev.status || "").trim() === "Closed";
                  const locationLabel = formatPlaceholderAwareEventLocation(ev);
                  return (
                    <tr key={`${ev._id}-${ev.participant_id || ""}`}>
                      <td>{ev.name || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatPlaceholderAwareEventDate(ev, formatTimestampForTable)}
                      </td>
                      <td>{getMyEventsParticipantDisplay(ev, userInfo?._id)}</td>
                      <td>{formatMyRegistrationStatusLabel(ev.attendance)}</td>
                      <td>{(ev.organizer_name || "").trim() || "—"}</td>
                      <td><EventPriceBadge event={ev} /></td>
                      <td title={locationLabel}>{locationLabel}</td>
                      <td><EventCalendarOpenClosedStatusBadge event={ev} /></td>
                      <td>
                        {!isEventClosed ? (
                          <RowActionIcons>
                            <button
                              type="button"
                              className="btn-link-danger"
                              style={iconBtnStyle}
                              title="Cancel registration"
                              aria-label={`Cancel registration for ${ev.name || "event"}`}
                              disabled={cancellingParticipantId === (ev.participant_id || "")}
                              onClick={() => cancelRegistration(ev)}
                            >
                              <IconCancel size={18} />
                            </button>
                          </RowActionIcons>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableShell>
        )}
        {!loading &&
          !registrationFamilyContextLoading &&
          activeTab === "available" &&
          eventListViewMode === "card" &&
          programFilteredAvailableEvents.map((ev) => {
          const isSelected = selectedEventIds.has(String(ev._id));
          const heroBgUrl = resolveEventBackgroundImageUrl(ev.background_image_url);
          const placeholder = isPlaceholderEvent(ev);
          return (
          <article
            key={ev._id}
            className={`card EventCalendar-card--hero${isSelected ? " card--selected" : ""}`}
          >
            <div
              className="EventCalendar-card-hero-banner"
              style={{
                backgroundImage: `url(${heroBgUrl})`,
              }}
              aria-hidden={true}
            />
            <div className="card-body">
              <div className="card-main">
                <div className="card-main-details">
                  <div className="card-date">
                    <IconCalendar />
                    {placeholder && !ev.start_datetime
                      ? EVENT_PLACEHOLDER_TBA
                      : ev.start_datetime
                        ? dayjs(ev.start_datetime).format("dddd, MMMM D, YYYY")
                        : "—"}
                  </div>
                  {ev.start_datetime && ev.end_datetime && (
                    <div className="card-time">
                      {dayjs(ev.start_datetime).format("h:mm A")} –{" "}
                      {dayjs(ev.end_datetime).format("h:mm A")}
                    </div>
                  )}
                  <h3 className="card-title">{ev.name || "Event"}</h3>
                  {ev.organizer_name && (
                    <div className="card-meta">
                      <span className="card-meta-label">Hosted By:</span>
                      <span>{ev.organizer_name}</span>
                    </div>
                  )}
                  {(ev.audience_type || "").trim() && (
                    <div className="card-meta">
                      <span className="card-meta-label">Audience Type:</span>
                      <span>{(ev.audience_type || "").trim()}</span>
                    </div>
                  )}
                  {(ev.perfect_for || "").trim() && (
                    <div className="card-meta">
                      <span className="card-meta-label">Perfect For:</span>
                      <span>{(ev.perfect_for || "").trim()}</span>
                    </div>
                  )}
                  <EventCardPriceMeta event={ev} />
                  {(ev.location || placeholder) && (
                    <div className="card-location" title={ev.location || EVENT_PLACEHOLDER_TBA}>
                      <IconLocation />
                      <span>{(ev.location || "").trim() || EVENT_PLACEHOLDER_TBA}</span>
                    </div>
                  )}
                  <EventDescriptionPreview
                    description={ev.description}
                    dialogTitle={ev.name ? `${ev.name} — Description` : "Description"}
                  />
                </div>
                {account_id && !placeholder && (
                  <div className="card-main-qr">
                    <EventRegistrationQRCode
                      event={ev}
                      accountId={account_id}
                      serverUrl={serverUrl}
                      size={120}
                      className="card-qr-wrap"
                    />
                  </div>
                )}
              </div>
              {!placeholder ? (
              <div className="card-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-primary EventCalendar-register-now-btn"
                  onClick={() => {
                    startBulkRegistration({ additionalEventIds: [ev._id] });
                  }}
                  style={{ whiteSpace: "nowrap" }}
                  aria-describedby="EventCalendar-bulk-register-hint"
                  aria-label={`Register now for ${ev.name || "event"}${isSelected ? "" : " and include any other selected events"}`}
                >
                  REGISTER NOW
                </button>
                {isSelected ? (
                  <button
                    type="button"
                    className="btn btn-sm EventCalendar-selection-btn EventCalendar-selection-btn--remove"
                    onClick={() => {
                      if (!user || !userInfo) {
                        openAlert(
                          "Account access is required to register. Log in or sign up to continue.",
                          "Account access required"
                        );
                        return;
                      }
                      toggleEventSelection(ev._id, false);
                    }}
                  >
                    <IconDelete size={16} style={{ marginRight: 6 }} />
                    REMOVE FROM SELECTION
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm EventCalendar-selection-btn EventCalendar-selection-btn--add"
                    onClick={() => {
                      if (!user || !userInfo) {
                        openAlert(
                          "Account access is required to register. Log in or sign up to continue.",
                          "Account access required"
                        );
                        return;
                      }
                      toggleEventSelection(ev._id, true);
                    }}
                  >
                    <IconSave size={16} style={{ marginRight: 6 }} />
                    ADD TO SELECTION
                  </button>
                )}
              </div>
              ) : null}
            </div>
          </article>
        );})}
        {!loading &&
          activeTab === "registered" &&
          eventListViewMode === "card" &&
          programFilteredRegisteredEvents.map((ev) => (
          <RegisteredEventHeroCard
            key={`${ev._id}-${ev.participant_id || ""}`}
            event={ev}
            participantLabel={getRegisteredEventParticipantChildLabel(ev, userInfo?._id)}
            onCancel={() => cancelRegistration(ev)}
            cancelling={cancellingParticipantId === (ev.participant_id || "")}
            accountId={account_id}
            serverUrl={serverUrl}
          />
        ))}
        {!loading &&
          activeTab === "participant-qr" &&
          user &&
          !registrationFamilyContextLoading &&
          participantQrEntries.length > 0 && (
            <ParticipantCheckInQRPanel
              familyEntries={participantQrEntries}
              registeredEventRowsForOptions={upcomingRegisteredEvents}
              allRegisteredEventRows={myRegisteredEvents}
              accountId={account_id}
              serverUrl={serverUrl}
              searchLower={eventListSearchLower}
            />
          )}
      </div>
      </div>

      {(modalEvent || bulkMode) && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="event-calendar-modal-title"
        >
          <div
            className="modal-content"
            ref={modalContentRef}
            onClick={(e) => e.stopPropagation()}
            onFocusCapture={(e) => {
              const id = e?.target?.id;
              if (id) lastFocusedInputIdRef.current = String(id);
            }}
            onInputCapture={(e) => {
              const t = e?.target;
              const id = t?.id;
              if (!id) return;
              lastFocusedInputIdRef.current = String(id);
              lastModalTypingAtRef.current = Date.now();
              if (typeof t.selectionStart === "number" && typeof t.selectionEnd === "number") {
                lastFocusedSelectionRef.current = {
                  id: String(id),
                  start: t.selectionStart,
                  end: t.selectionEnd,
                  direction: t.selectionDirection || "none",
                  value: typeof t.value === "string" ? t.value : String(t.value || ""),
                };
              }
            }}
            onKeyUpCapture={(e) => {
              const t = e?.target;
              const id = t?.id;
              if (!id) return;
              lastFocusedInputIdRef.current = String(id);
              lastModalTypingAtRef.current = Date.now();
              if (typeof t.selectionStart === "number" && typeof t.selectionEnd === "number") {
                lastFocusedSelectionRef.current = {
                  id: String(id),
                  start: t.selectionStart,
                  end: t.selectionEnd,
                  direction: t.selectionDirection || "none",
                  value: typeof t.value === "string" ? t.value : String(t.value || ""),
                };
              }
            }}
          >
            <h3 id="event-calendar-modal-title">
              {bulkMode
                ? "Register for selected events"
                : `Register for ${modalEvent.name}`}
            </h3>
            <div className="modal-event-details">
                  {!bulkMode && modalEvent && (
                <>
                  <div className="modal-detail">
                    <span className="modal-detail-label">Price:</span>
                    <EventPriceBadge event={modalEvent} />
                  </div>
                  {(modalEvent.perfect_for || "").trim() && (
                    <div className="modal-detail">
                      <span className="modal-detail-label">Perfect For:</span>
                      <span>{(modalEvent.perfect_for || "").trim()}</span>
                    </div>
                  )}
                  {(modalEvent.start_datetime || modalEvent.location) && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                        Key Event Details
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                          flexWrap: "wrap",
                          background: "#f3f4f6",
                          borderRadius: 10,
                          padding: "10px 12px",
                        }}
                      >
                        {modalEvent.start_datetime && (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 180 }}>
                            <IconCalendar size={18} />
                            <div style={{ fontWeight: 600 }}>
                              {dayjs(modalEvent.start_datetime).format("ddd, MMM D, YYYY")}
                            </div>
                          </div>
                        )}
                        {modalEvent.start_datetime && modalEvent.end_datetime && (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 180 }}>
                            <IconClock size={18} />
                            <div style={{ fontWeight: 600 }}>
                              {dayjs(modalEvent.start_datetime).format("h:mm A")} –{" "}
                              {dayjs(modalEvent.end_datetime).format("h:mm A")}
                            </div>
                          </div>
                        )}
                        {modalEvent.location && (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 240 }}>
                            <IconLocation size={18} />
                            <div style={{ fontWeight: 600 }}>{modalEvent.location}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {modalEvent.description && (
                    <div className="modal-detail modal-detail-description">
                      <span className="modal-detail-label">
                        Description:
                      </span>
                      <span className="modal-description-text">
                        {modalEvent.description}
                      </span>
                    </div>
                  )}
                </>
              )}
              {bulkMode && bulkEvents.length > 0 && (
                <>
                  <div className="modal-detail">
                    <span className="modal-detail-label">
                      Events selected:
                    </span>
                    <span>{bulkEvents.length}</span>
                  </div>
                  {!(user && userInfo) && (
                    <ul className="modal-bulk-list">
                      {bulkEvents.map((ev) => (
                        <li key={ev._id}>
                          <strong>{ev.name || "Event"}</strong>
                          {ev.start_datetime && (
                            <span>
                              {" "}
                              –{" "}
                              {dayjs(ev.start_datetime).format("MMM D, YYYY")}
                              {ev.end_datetime && (
                                <>
                                  {" "}
                                  ·{" "}
                                  {dayjs(ev.start_datetime).format("h:mm A")} –{" "}
                                  {dayjs(ev.end_datetime).format("h:mm A")}
                                </>
                              )}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="modal-bulk-hint">
                    {user && userInfo
                      ? guestMode === "parent"
                        ? shouldIncludeParentSelfAttendee(bulkEvents)
                          ? "Choose which participants (you and/or your children) attend each event in the table below. At least one participant must be selected for every event."
                          : "Choose which children attend each event in the table below. At least one child must be selected for every event."
                        : "You will be registered for each event column below (read-only)."
                      : "One registration will be created for each selected event using the details you enter below."}
                  </p>
                </>
              )}
            </div>

            {!(user && userInfo) && (
              <div className="modal-form EventCalendar-modal-form">
                <p className="prefill-note" style={{ marginBottom: 10 }}>
                  Account access is required to register. Log in or sign up to continue.
                </p>
                <div className="modal-actions">
                  <button type="button" className="secondary" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => openProfileSignup({ mode: "choice" })}
                    disabled={submitting || eventSignupSettingsLoading || !signupOptionsAvailable}
                  >
                    Sign Up
                  </button>
                  <button type="button" className="primary" onClick={goToEventCalendarLogin} disabled={submitting}>
                    Log In
                  </button>
                </div>
              </div>
            )}

            {user && userInfo && attendStep === "guest-form" && (
              <form
                onSubmit={
                  guestMode === "parent"
                    ? (bulkMode ? submitParentRegistrationBulk : submitParentRegistrationSingle)
                    : (bulkMode ? submitBulkRegistration : submitRegistration)
                }
                className="modal-form EventCalendar-modal-form"
              >
                {guestMode === "self" ? (
                  <>
                    {/* Logged-in self: review-only (no in-modal editing). */}
                    {user && userInfo ? (
                      <>
                        <div
                          className="cards-wrapper cards-wrapper--sm"
                          style={{
                            gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
                            gap: 12,
                            marginTop: 10,
                            marginBottom: 10,
                          }}
                        >
                          {/* Child / participant card (emerald edge) */}
                          <div className="card" style={{ borderLeftColor: "#10B981" }} aria-label="Participant">
                            <div className="card-header">
                              <div className="card-header-content">
                                <div className="card-title">{formatSelfDisplayName(form)}</div>
                              </div>
                            </div>
                            <div className="card-meta-row">
                              <span className="card-meta-emphasis">Grade:</span>{" "}
                              <span>{getGradeLabel(form.grade)}</span>
                              <span>·</span>
                              <span className="card-meta-emphasis">School:</span>{" "}
                              <span>{(form.school || "").trim() || "—"}</span>
                            </div>
                            {(((form.phone || "").trim() || displayEmailOrDash(form.email) !== "—")) ? (
                              <div className="card-meta-muted">
                                {((form.phone || "").trim() ? `Phone: ${(form.phone || "").trim()}` : "Phone: —")}
                                {" · "}
                                {`Email: ${displayEmailOrDash(form.email)}`}
                              </div>
                            ) : (
                              <div className="card-meta-muted">—</div>
                            )}
                            <div className="card-meta-row">
                              <span className="card-meta-emphasis">Role:</span> <span>Participant</span>
                            </div>
                          </div>

                          {/* Parent / guardian card (orange edge) when youth-only context applies */}
                          {selfFlowYouthOnlyContext ? (
                            <>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginTop: 6 }}>
                                Parent / Guardian
                              </div>
                              <div className="card" aria-label="Parent / guardian">
                              <div className="card-header">
                                <div className="card-header-content">
                                  <div className="card-title">
                                    {[(form.parent_first_name || "").trim(), (form.parent_last_name || "").trim()]
                                      .filter(Boolean)
                                      .join(" ") || "—"}
                                  </div>
                                </div>
                              </div>
                              <div className="card-meta-muted">
                                {displayEmailOrDash(form.parent_email)}{" "}
                                {" · "}
                                {(form.parent_phone || "").trim() || "—"}
                              </div>
                              <div className="card-meta-row">
                                <span className="card-meta-emphasis">Role:</span> <span>Parent / Guardian</span>
                              </div>
                            </div>
                            </>
                          ) : null}
                        </div>
                        {bulkMode && bulkEvents.length > 0 && (
                          <div style={{ marginTop: 12, overflowX: "auto" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: 13,
                                border: "1px solid #dee2e6",
                              }}
                            >
                              <thead>
                                <tr style={{ background: "#f3f4f6" }}>
                                  <th
                                    scope="col"
                                    style={{
                                      textAlign: "left",
                                      padding: "8px 10px",
                                      borderBottom: "1px solid #dee2e6",
                                      fontWeight: 700,
                                    }}
                                  >
                                    Participant
                                  </th>
                                  {(bulkEvents || []).map((ev) => (
                                    <th
                                      key={ev._id}
                                      scope="col"
                                      style={{
                                        textAlign: "center",
                                        padding: "8px 6px",
                                        borderBottom: "1px solid #dee2e6",
                                        borderLeft: "1px solid #dee2e6",
                                        fontWeight: 700,
                                        minWidth: 100,
                                      }}
                                    >
                                      <div>{ev.name || "Event"}</div>
                                      {ev.start_datetime && (
                                        <div style={{ fontWeight: 400, fontSize: 12, color: "#4b5563", marginTop: 4 }}>
                                          {dayjs(ev.start_datetime).format("MMM D, h:mm A")}
                                        </div>
                                      )}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td
                                    style={{
                                      padding: "8px 10px",
                                      borderTop: "1px solid #dee2e6",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    {formatSelfDisplayName(form)}
                                  </td>
                                  {(bulkEvents || []).map((ev) => (
                                    <td
                                      key={ev._id}
                                      style={{
                                        textAlign: "center",
                                        borderLeft: "1px solid #dee2e6",
                                        borderTop: "1px solid #dee2e6",
                                        background: "#f8fafc",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked
                                        disabled
                                        aria-label={`Registered for ${ev.name || "event"}`}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {renderSelfContactFieldRows()}
                        {selfFlowYouthOnlyContext && (
                          <>
                            {renderSelfYouthFieldRows(true)}
                            <p className="prefill-note" style={{ marginTop: "6px" }}>
                              Parent / Guardian
                            </p>
                            {renderSelfParentGuardianFieldRows(true)}
                          </>
                        )}
                      </>
                    )}
                    <div className="modal-actions">
                      <button type="button" className="secondary" onClick={closeModal}>
                        Cancel
                      </button>
                      <button type="submit" className="primary" disabled={submitting}>
                        {submitting
                          ? "Registering…"
                          : bulkMode && bulkEvents.length > 0
                            ? `Register (${bulkEvents.length})`
                            : "Register"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Logged-in parent: review-only (no in-modal editing). Guest parent: show contact fields. */}
                    {user && userInfo ? (
                      <div
                        className="cards-wrapper cards-wrapper--sm"
                        style={{
                          gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
                          gap: 12,
                          marginTop: 10,
                          marginBottom: 10,
                        }}
                      >
                        <div className="card" aria-label="Parent contact">
                          <div className="card-header">
                            <div className="card-header-content">
                              <div className="card-title">
                                {[(form.parent_first_name || "").trim(), (form.parent_last_name || "").trim()].filter(Boolean).join(" ") || "—"}
                              </div>
                            </div>
                          </div>
                          <div className="card-meta-muted">
                            {displayEmailOrDash(form.parent_email)}
                            {" · "}
                            {(form.parent_phone || "").trim() || "—"}
                          </div>
                          <div className="card-meta-row">
                            <span className="card-meta-emphasis">Role:</span> <span>Parent / Guardian</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      renderParentContactFieldRows()
                    )}

                    {(() => {
                      if (user && userInfo && bulkMode && guestMode === "parent") {
                        const regMap = registeredParticipantsByEventId;
                        const evs = bulkEvents || [];
                        const eventIdList = evs.map((e) => String(e._id));
                        const includesParentSelf = (children || []).some((c) => c?.is_parent_self);
                        const nRegistrations = countBulkParentRegistrationSelections(children, evs, regMap);
                        const hasToggleableCells = bulkParentHasAnyToggleableCell(
                          children,
                          evs,
                          regMap
                        );
                        const allToggleableChecked = bulkParentAllToggleableCellsChecked(
                          children,
                          evs,
                          regMap
                        );
                        const bulkParentSubmitDisabled =
                          submitting ||
                          nRegistrations === 0 ||
                          evs.some((ev) => {
                            const eid = String(ev._id);
                            const ok = (children || []).some(
                              (c) =>
                                !!(c?.bulk_event_attend || {})[eid] &&
                                bulkParentCellIsToggleable(c, ev, regMap)
                            );
                            return !ok;
                          });
                        return (
                          <>
                            {(children || []).length === 0 ? (
                              <div className="card" style={{ marginTop: 10 }}>
                                <div style={{ color: "#6c757d" }}>
                                  No linked children found. Add children on the <Link to="/Children">Children</Link> page.
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ marginTop: 10 }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 12,
                                      background: "#eaf3ff",
                                      borderRadius: 8,
                                      padding: "6px 10px",
                                      border: "1px solid #cfe3ff",
                                    }}
                                  >
                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1f4fd6" }}>
                                      {includesParentSelf ? "Participants" : "Children"}
                                    </div>
                                    <button
                                      type="button"
                                      className="secondary"
                                      style={{ padding: "4px 10px", fontSize: 12 }}
                                      disabled={
                                        submitting || eventIdList.length === 0 || !hasToggleableCells
                                      }
                                      onClick={() => {
                                        const turnOn = !allToggleableChecked;
                                        setChildren((prev) =>
                                          (prev || []).map((c) => {
                                            const m = { ...(c.bulk_event_attend || {}) };
                                            for (const ev of evs) {
                                              const eid = String(ev._id);
                                              if (!bulkParentCellIsToggleable(c, ev, regMap)) continue;
                                              m[eid] = turnOn;
                                            }
                                            const attendsAny = evs.some(
                                              (ev) =>
                                                !!(m[String(ev._id)]) &&
                                                bulkParentCellIsToggleable(c, ev, regMap)
                                            );
                                            return { ...c, bulk_event_attend: m, attend: attendsAny };
                                          })
                                        );
                                      }}
                                    >
                                      {allToggleableChecked ? "Unselect all" : "Select all"}
                                    </button>
                                  </div>
                                </div>
                                <div style={{ marginTop: 10, overflowX: "auto" }}>
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                      fontSize: 13,
                                      border: "1px solid #dee2e6",
                                    }}
                                  >
                                    <thead>
                                      <tr style={{ background: "#f3f4f6" }}>
                                        <th
                                          scope="col"
                                          style={{
                                            textAlign: "left",
                                            padding: "8px 10px",
                                            borderBottom: "1px solid #dee2e6",
                                            fontWeight: 700,
                                            minWidth: 160,
                                          }}
                                        >
                                          {includesParentSelf ? "Participant" : "Child"}
                                        </th>
                                        {evs.map((ev) => (
                                          <th
                                            key={ev._id}
                                            scope="col"
                                            style={{
                                              textAlign: "center",
                                              padding: "8px 6px",
                                              borderBottom: "1px solid #dee2e6",
                                              borderLeft: "1px solid #dee2e6",
                                              fontWeight: 700,
                                              minWidth: 100,
                                            }}
                                          >
                                            <div>{ev.name || "Event"}</div>
                                            {ev.start_datetime && (
                                              <div
                                                style={{
                                                  fontWeight: 400,
                                                  fontSize: 12,
                                                  color: "#4b5563",
                                                  marginTop: 4,
                                                }}
                                              >
                                                {dayjs(ev.start_datetime).format("MMM D, h:mm A")}
                                              </div>
                                            )}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(children || []).map((c) => (
                                        <tr key={c.id}>
                                          <td
                                            style={{
                                              padding: "8px 10px",
                                              borderTop: "1px solid #dee2e6",
                                              verticalAlign: "middle",
                                            }}
                                          >
                                            <div style={{ fontWeight: 600 }}>{formatChildRegistrantTitle(c)}</div>
                                            <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
                                              {c.is_parent_self
                                                ? "Parent / Guardian"
                                                : `${getGradeLabel(c.grade)} · ${(c.school || "").trim() || "—"}`}
                                            </div>
                                          </td>
                                          {evs.map((ev) => {
                                            const eid = String(ev._id);
                                            const already = childAlreadyRegisteredForEvent(c, eid, regMap);
                                            const parentNotEligible =
                                              !!c.is_parent_self && !parentIsRegistrableAttendeeForEvent(ev);
                                            const checked = !!(c.bulk_event_attend || {})[eid];
                                            return (
                                              <td
                                                key={ev._id}
                                                style={{
                                                  textAlign: "center",
                                                  borderLeft: "1px solid #dee2e6",
                                                  borderTop: "1px solid #dee2e6",
                                                  background: already || parentNotEligible ? "#f1f5f9" : "#fff",
                                                }}
                                              >
                                                {already || parentNotEligible ? (
                                                  <span style={{ fontSize: 12, color: "#64748b" }}>—</span>
                                                ) : (
                                                  <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={submitting}
                                                    aria-label={`${formatChildRegistrantTitle(c)} for ${ev.name || "event"}`}
                                                    onChange={(e) => {
                                                      const nextChecked = !!e.target.checked;
                                                      setChildren((prev) =>
                                                        (prev || []).map((x) => {
                                                          if (x.id !== c.id) return x;
                                                          const m = { ...(x.bulk_event_attend || {}) };
                                                          m[eid] = nextChecked;
                                                          const attendsAny = evs.some(
                                                            (ev2) =>
                                                              !!(m[String(ev2._id)]) &&
                                                              bulkParentCellIsToggleable(x, ev2, regMap)
                                                          );
                                                          return { ...x, bulk_event_attend: m, attend: attendsAny };
                                                        })
                                                      );
                                                    }}
                                                  />
                                                )}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                            <div className="modal-actions">
                              <button type="button" className="secondary" onClick={closeModal}>
                                Cancel
                              </button>
                              <button type="submit" className="primary" disabled={bulkParentSubmitDisabled}>
                                {submitting
                                  ? "Registering…"
                                  : nRegistrations > 0
                                    ? `Register (${nRegistrations})`
                                    : "Register"}
                              </button>
                            </div>
                          </>
                        );
                      }

                      const eligible = (children || []).filter((c) => !c?.already_registered_for_event);
                      const selectedEligibleCount = eligible.filter((c) => !!c.attend).length;
                      const allEligibleSelected = eligible.length > 0 && selectedEligibleCount === eligible.length;
                      const includesParentSelfSingle = (children || []).some((c) => c?.is_parent_self);
                      const attendeesHeading = includesParentSelfSingle ? "Participants" : "Children";

                      return (
                        <>
                          {user && userInfo && (
                            <div style={{ marginTop: 10 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 12,
                                  background: "#eaf3ff",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  border: "1px solid #cfe3ff",
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#1f4fd6" }}>{attendeesHeading}</div>
                                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, margin: 0, fontSize: 13 }}>
                                  <input
                                    type="checkbox"
                                    checked={allEligibleSelected}
                                    disabled={eligible.length === 0}
                                    onChange={(e) => {
                                      const checked = !!e.target.checked;
                                      setChildren((prev) =>
                                        (prev || []).map((x) =>
                                          x?.already_registered_for_event ? x : { ...x, attend: checked }
                                        )
                                      );
                                    }}
                                  />
                                  <span>Select All</span>
                                </label>
                              </div>
                            </div>
                          )}

                          <div
                            className="cards-wrapper cards-wrapper--sm"
                            style={{
                              marginTop: 10,
                              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            }}
                          >
                            {user && userInfo ? (
                              (children || []).length === 0 ? (
                                <div className="card">
                                  <div style={{ color: "#6c757d" }}>
                                    {includesParentSelfSingle
                                      ? "No participants available to register."
                                      : <>No linked children found. Add children on the <Link to="/Children">Children</Link> page.</>}
                                  </div>
                                </div>
                              ) : eligible.length === 0 ? (
                                <div className="card">
                                  <div style={{ color: "#6c757d" }}>
                                    {includesParentSelfSingle
                                      ? "No eligible participants to register (everyone is already registered)."
                                      : "No eligible children to register (all linked children are already registered)."}
                                  </div>
                                </div>
                              ) : (
                                eligible.map((c, idx) => (
                                  <div
                                    key={c.id}
                                    className="card"
                                    style={{
                                      borderLeftColor: c.attend ? "#2563EB" : "#94A3B8",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => {
                                      setChildren((prev) =>
                                        prev.map((x) => (x.id === c.id ? { ...x, attend: !x.attend } : x))
                                      );
                                    }}
                                  >
                                    <div className="card-header">
                                      <input
                                        type="checkbox"
                                        checked={!!c.attend}
                                        disabled={false}
                                        aria-label={`${c.is_parent_self ? "Parent" : `Child ${idx + 1}`} attending`}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          setChildren((prev) =>
                                            prev.map((x) => (x.id === c.id ? { ...x, attend: e.target.checked } : x))
                                          );
                                        }}
                                      />
                                      <div className="card-header-content">
                                        <div className="card-title">
                                          {formatChildRegistrantTitle(c)}
                                        </div>
                                      </div>
                                    </div>
                                    {!c.is_parent_self && (
                                      <div className="card-meta-row">
                                        <span className="card-meta-emphasis">Grade:</span>{" "}
                                        <span>{getGradeLabel(c.grade)}</span>
                                        <span>·</span>
                                        <span className="card-meta-emphasis">School:</span>{" "}
                                        <span>{(c.school || "").trim() || "—"}</span>
                                      </div>
                                    )}
                                    <div className="card-meta-row">
                                      <span className="card-meta-emphasis">Role:</span>{" "}
                                      <span>{c.is_parent_self ? "Parent / Guardian" : "Participant"}</span>
                                    </div>
                                    {(() => {
                                      const emailDisp = displayEmailOrDash(c.email);
                                      const phoneTrim = (c.phone || "").trim();
                                      const show = emailDisp !== "—" || !!phoneTrim;
                                      if (!show) return null;
                                      return (
                                        <div className="card-meta-muted">
                                          {`Phone: ${phoneTrim || "—"}`}
                                          {" · "}
                                          {`Email: ${emailDisp}`}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ))
                              )
                            ) : (
                              (children || []).map((c, idx) => {
                          const youthOnlyContext =
                            (!bulkMode && modalEvent && isYouthOnlyEvent(modalEvent)) ||
                            (bulkMode && bulkEvents.length > 0 && bulkEvents.some(isYouthOnlyEvent));
                          return (
                            <div key={c.id} style={{ border: "1px solid #e9ecef", borderRadius: 6, padding: "8px 10px", background: "#fff" }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    margin: 0,
                                    flexWrap: "nowrap",
                                    minWidth: "min-content",
                                    opacity: c.already_registered_for_event ? 0.85 : 1,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!c.attend}
                                    disabled={!!c.already_registered_for_event}
                                    onChange={(e) => {
                                      if (c.already_registered_for_event) return;
                                      setChildren((prev) =>
                                        prev.map((x) => (x.id === c.id ? { ...x, attend: e.target.checked } : x))
                                      );
                                    }}
                                  />
                                  <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "nowrap", gap: 8, whiteSpace: "nowrap", flexShrink: 0 }}>
                                    <strong>Child {idx + 1}</strong>
                                  </span>
                                </label>
                                <button
                                  type="button"
                                  className="secondary"
                                  onClick={() => setChildren((prev) => prev.filter((x) => x.id !== c.id))}
                                  disabled={(children || []).length <= 1}
                                  style={{ padding: "4px 10px", fontSize: 12, flexShrink: 0 }}
                                  title={(children || []).length <= 1 ? "At least one child is required" : "Remove child"}
                                >
                                  Remove
                                </button>
                              </div>
                              {renderChildDetailFields(c, youthOnlyContext)}
                            </div>
                          );
                              })
                            )}
                          </div>

                          <div className="modal-actions">
                            <button type="button" className="secondary" onClick={closeModal}>
                              Cancel
                            </button>
                            <button type="submit" className="primary" disabled={submitting}>
                              {submitting
                                ? "Registering…"
                                : user && userInfo
                                  ? `Register ${selectedEligibleCount} People`
                                  : "Register"}
                            </button>
                          </div>
                        </>
                      );
                    })()}
                    {!(user && userInfo) && (
                      <button
                        type="button"
                        className="EventCalendar-add-another-child-btn"
                        onClick={() => setChildren((prev) => [...prev, emptyChild()])}
                        disabled={submitting}
                      >
                        <span aria-hidden="true">+</span>
                        Add another child
                      </button>
                    )}
                  </>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      <ModalShell
        open={profileSignupOpen}
        onClose={submitting ? undefined : closeProfileSignup}
        title="Sign Up"
        maxWidth={ACCOUNT_SIGNUP_MODAL_MAX_WIDTH}
        zIndex={1000}
        panelRef={modalContentRef}
        closeOnOverlayClick={!submitting}
        closeOnEscape={!submitting}
        bodyClassName={ACCOUNT_SIGNUP_FORM_CLASS}
        bodyProps={{
          onFocusCapture: (e) => {
            const id = e?.target?.id;
            if (id) lastFocusedInputIdRef.current = String(id);
          },
          onInputCapture: (e) => {
            const t = e?.target;
            const id = t?.id;
            if (!id) return;
            lastFocusedInputIdRef.current = String(id);
            lastModalTypingAtRef.current = Date.now();
            if (typeof t.selectionStart === "number" && typeof t.selectionEnd === "number") {
              lastFocusedSelectionRef.current = {
                id: String(id),
                start: t.selectionStart,
                end: t.selectionEnd,
                direction: t.selectionDirection || "none",
                value: typeof t.value === "string" ? t.value : String(t.value || ""),
              };
            }
          },
          onKeyUpCapture: (e) => {
            const t = e?.target;
            const id = t?.id;
            if (!id) return;
            lastFocusedInputIdRef.current = String(id);
            lastModalTypingAtRef.current = Date.now();
            if (typeof t.selectionStart === "number" && typeof t.selectionEnd === "number") {
              lastFocusedSelectionRef.current = {
                id: String(id),
                start: t.selectionStart,
                end: t.selectionEnd,
                direction: t.selectionDirection || "none",
                value: typeof t.value === "string" ? t.value : String(t.value || ""),
              };
            }
          },
        }}
        footer={
          gatewayMode === "choice" ? (
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={closeProfileSignup} disabled={submitting}>
                Cancel
              </button>
            </div>
          ) : gatewayMode === "signup-self" || gatewayMode === "signup-parent" ? (
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setGatewayMode("choice")} disabled={submitting}>
                Back
              </button>
              <button type="button" className="secondary" onClick={closeProfileSignup} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleGatewayCreateAccount}
                disabled={submitting || (gatewayMode === "signup-parent" && !coppaPrivacyConsent)}
              >
                {submitting ? "Creating…" : "Create account"}
              </button>
            </div>
          ) : null
        }
      >
        {gatewayMode === "choice" && (
          <>
            <div style={{ marginTop: 6, marginBottom: 10, fontSize: 16, fontWeight: 600 }}>
              {signupChoiceSubtitle}
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "stretch", maxWidth: "100%" }}>
              {allowStudentYouthSignup && (
                <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setGatewayMode("signup-self")}
                    disabled={submitting}
                    style={EC_GATEWAY_CHOICE_PILL_STYLE}
                  >
                    Create Account (Student / Youth)
                  </button>
                  <div style={{ marginTop: 10, fontSize: 13, color: "#4b5563", textAlign: "center", lineHeight: 1.35 }}>
                    Fast, one-time setup for registering yourself.
                  </div>
                </div>
              )}

              {allowFamilyParentSignup && (
                <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setGatewayMode("signup-parent")}
                    disabled={submitting}
                    style={EC_GATEWAY_CHOICE_PILL_STYLE}
                  >
                    Create Account (Parent/Guardian/Caregiver)
                  </button>
                  <div style={{ marginTop: 10, fontSize: 13, color: "#4b5563", textAlign: "center", lineHeight: 1.35 }}>
                    Set up your profile to manage and register multiple family members.
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginTop: 14, color: "#111827", fontSize: 13, lineHeight: 1.4 }}>
              {allowStudentYouthSignup && allowFamilyParentSignup
                ? 'Both "Create Account" options are a required one-time setup to securely save your details and manage current and future registrations.'
                : "Create Account is a required one-time setup to securely save your details and manage current and future registrations."}
            </div>
          </>
        )}

        {(gatewayMode === "signup-self" || gatewayMode === "signup-parent") && (
          <>
            <p className="prefill-note" style={{ marginBottom: 10 }}>
              One-time setup. Create your account and set your password now.
            </p>

            {gatewayMode === "signup-self" ? (
              <>
                {renderSelfContactFieldRows()}
                {renderGatewayPasswordFieldRow()}
                <p className="prefill-note" style={{ marginTop: "6px" }}>
                  Youth
                </p>
                {renderSelfYouthFieldRows(true)}
                <p className="prefill-note" style={{ marginTop: "6px" }}>
                  Parent / Guardian
                </p>
                {renderSelfParentGuardianFieldRows(true)}
              </>
            ) : (
              <>
                <p className="prefill-note" style={{ marginTop: "6px" }}>
                  Parent/Guardian/Caregiver
                </p>
                {renderParentContactFieldRows()}
                {renderGatewayPasswordFieldRow()}
                <p className="prefill-note" style={{ marginTop: "6px" }}>
                  Children attending
                </p>
                <div className="EventCalendar-reg-children-stack">
                  {(children || []).map((c, idx) => (
                    <div key={c.id} style={{ border: "1px solid #e9ecef", borderRadius: 6, padding: "10px 12px", background: "#fff" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                        <strong>Child {idx + 1}</strong>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => setChildren((prev) => prev.filter((x) => x.id !== c.id))}
                          disabled={submitting || (children || []).length <= 1}
                          style={{ padding: "4px 10px", fontSize: 12, flexShrink: 0 }}
                          title={(children || []).length <= 1 ? "At least one child is required" : "Remove child"}
                        >
                          Remove
                        </button>
                      </div>
                      {renderChildDetailFields(c, true)}
                      <PhotographyConsentBlock
                        idPrefix={`ec-signup-photo-${c.id}`}
                        value={c.photography_consent}
                        variant={idx % 2 === 0 ? "warm" : "cool"}
                        disabled={submitting}
                        onChange={(next) =>
                          setChildren((prev) =>
                            prev.map((x) => (x.id === c.id ? { ...x, photography_consent: next } : x))
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="EventCalendar-add-another-child-btn"
                  onClick={() => setChildren((prev) => [...prev, emptyChild()])}
                  disabled={submitting}
                >
                  <span aria-hidden="true">+</span>
                  Add another child
                </button>
                <CoppaPrivacyConsentBlock
                  checkboxId="ec-signup-coppa-consent"
                  checked={coppaPrivacyConsent}
                  onChange={setCoppaPrivacyConsent}
                />
              </>
            )}

            {emailCheckStatus.checking && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>Checking email availability…</div>
            )}
            {!emailCheckStatus.checking && emailCheckStatus.inUse && (
              <div style={{ fontSize: 12, color: "#b42318", marginTop: 6 }}>{emailCheckStatus.message}</div>
            )}
          </>
        )}
      </ModalShell>
    </div>
  );
}

export default EventCalendar;
